import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { INVENTORY, EVENT_TYPES, PACKAGE_PRESETS, calcLine } from '../data/inventory'

// ─── helpers ───────────────────────────────────────────────────────────────
const fmtINR = (n) =>
  '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })
const todayISO = () => new Date().toISOString().slice(0, 10)
const showNum = (v) => (v === 0 || v === '0' || v == null || v === '' ? '' : v)
const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const uid = (p = 'c') => p + '-' + Math.random().toString(36).slice(2, 9)

// Normalise an inclusion entry (string or {text,section}) and group items under
// their preceding section heading so "Complimentary" renders as a heading.
// Words that are always headings, never priced sub-items.
const SECTION_WORDS = ['complimentary', 'complementary', 'complimentary:', 'add-ons', 'add ons', 'inclusions', 'free']
const normItem = (x) => {
  if (typeof x === 'string') {
    return { text: x, section: SECTION_WORDS.includes(x.trim().toLowerCase()), bold: false }
  }
  const text = x?.text ?? ''
  // Even an explicit item flagged false is forced to a section if its text is a heading word.
  const section = !!x?.section || SECTION_WORDS.includes(String(text).trim().toLowerCase())
  return { text, section, bold: !!x?.bold }
}
const groupItems = (items = []) => {
  const groups = []
  let cur = null
  items.map(normItem).forEach(({ text, section, bold }) => {
    if (section) {
      cur = { section: text, entries: [] }
      groups.push(cur)
    } else {
      if (!cur) {
        cur = { section: null, entries: [] }
        groups.push(cur)
      }
      cur.entries.push({ text, bold })
    }
  })
  return groups
}
const fmtTime12 = (t) => {
  if (!t) return ''
  const [h, m] = String(t).split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return String(t)
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`
}
// URL-safe base64 encode/decode of the quote state (for share links).
const encodeShare = (state) => {
  try {
    const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(state))))
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
  } catch {
    return ''
  }
}
const decodeShare = (encoded) => {
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(decodeURIComponent(escape(atob(padded))))
  } catch {
    return null
  }
}

const generateQuoteNo = () => {
  const ts = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14)
  // 3 random digits (100–999) — keeps it unique without ever spelling a word.
  const suffix = String(Math.floor(Math.random() * 900) + 100)
  return `Q-${ts}-${suffix}`
}

// v2 catalogs, derived from the shared inventory.
// Catering = food courses only. Beverages/Stalls now live under "Snacks & Stalls".
const findItem = (id) => INVENTORY.find((i) => i.id === id)
const CATERING_CATS = ['Main Course', 'Starters', 'Desserts', 'Inclusive', 'Service']
// v2-only rate overrides (keeps shared inventory / v1 pricing untouched).
const CAT_OVERRIDE = {
  'biriyani-chicken': { rate: 1300, serves: 7.5 },
  'biriyani-mutton': { serves: 7.5 },
  'chicken-65': { rate: 500 },
  'pepper-chicken': { rate: 500 },
  kheer: { rate: 350, name: 'Phirni' },
  raitha: { name: 'Onion Raita' },
  brinjal: { name: 'Brinjal Dalcha' },
}
const CATERING_ITEMS = INVENTORY.filter(
  (i) => i.serviceType === 'Catering' && CATERING_CATS.includes(i.category),
).map((i) => (CAT_OVERRIDE[i.id] ? { ...i, ...CAT_OVERRIDE[i.id] } : i))

// Dedicated Veg dishes (v2-only; not in shared inventory).
const mk = (id, name, category, rate, serves) => ({ id, name, category, rule: 'per_unit_serves', unitLabel: 'Kg', rate, serves, serviceType: 'Catering' })
const VEG_ITEMS = [
  mk('veg-biriyani', 'Veg Biriyani', 'Main Course', 800, 7.5),
  mk('mushroom-biriyani', 'Mushroom Biriyani', 'Main Course', 900, 7.5),
  mk('palakai-biriyani', 'Palakai Biriyani', 'Main Course', 1000, 7.5),
  mk('paneer-65', 'Paneer 65', 'Starters', 600, 12),
  mk('gobi-65', 'Gobi 65', 'Starters', 400, 12),
]
// Synthetic (v2-only) items that live in Common but aren't in shared inventory.
const CAT_COMMON_EXTRA = {
  'potato-chips': mk('potato-chips', 'Potato Chips', 'Starters', 400, 12),
}
// Common (shared, priced at total guests) — display order matters.
const CAT_COMMON_IDS = ['kheer', 'idiyappam', 'bread-halwa', 'raitha', 'brinjal', 'potato-chips', 'banana-leaf', 'water-bottle', 'paper-roll', 'catering-service']
// Selected by default within Common (potato-chips, idiyappam & kheer available but off by default).
const COMMON_DEFAULT_ON = new Set(['raitha', 'brinjal', 'bread-halwa', 'paper-roll', 'banana-leaf', 'water-bottle', 'catering-service'])
// Bold by default within Common.
const COMMON_BOLD = new Set(['bread-halwa', 'raitha', 'brinjal'])
const CAT_COMMON_ITEMS = CAT_COMMON_IDS.map((id) => CAT_COMMON_EXTRA[id] || CATERING_ITEMS.find((i) => i.id === id)).filter(Boolean)
const CAT_NONVEG_FOOD = CATERING_ITEMS.filter((i) => !['Inclusive', 'Service'].includes(i.category) && !CAT_COMMON_IDS.includes(i.id))
// Section catalogs
const NONVEG_CATALOG = CAT_NONVEG_FOOD
const VEG_CATALOG = VEG_ITEMS

// Snacks & Stalls — consolidated: single Fruit Salad, single Icecream, no Rosemilk.
// Flavours/types are captured via each item's editable description field.
const SNACKS_CATS = ['Beverages', 'Stalls']
const SNACKS_ITEMS = [
  { ...findItem('welcome-juice'), name: 'Welcome Juice', description: 'Watermelon' },
  { ...findItem('fruit-salad-4'), name: 'Fruit Salad', description: '' },
  { ...findItem('icecream'), name: 'Icecream', description: 'Vanilla' },
  findItem('live-coffee-stall'),
  findItem('beeda'),
  findItem('panipuri'),
  findItem('spring-potato-roll'),
  findItem('sweet-corn'),
  findItem('popcorn-cotton-candy'),
  findItem('chocolate-fountain'),
].filter(Boolean)

// Service definitions — order here is the order steps appear in.
const SERVICES = [
  { key: 'Catering', icon: 'fa-utensils' },
  { key: 'Snacks & Stalls', icon: 'fa-ice-cream' },
  { key: 'Decoration', icon: 'fa-wand-magic-sparkles' },
  { key: 'Photography', icon: 'fa-camera' },
  { key: 'Entertainment', icon: 'fa-masks-theater' },
  { key: 'Cake', icon: 'fa-cake-candles' },
  { key: 'Return Gifts', icon: 'fa-gift' },
]

// Entertainment options — multi-select, each editable (name/price/qty/desc/bold).
const entDefault = () => [
  { id: 'ent-dj', name: 'DJ', price: 8000, qty: 1, desc: '', bold: false, on: false },
  { id: 'ent-mascots', name: 'Mascots', price: 4000, qty: 1, desc: '', bold: false, on: false },
  { id: 'ent-photobooth', name: 'Photobooth', price: 9000, qty: 1, desc: '', bold: false, on: false },
  { id: 'ent-videobooth', name: 'Videobooth', price: 7000, qty: 1, desc: '', bold: false, on: false },
  { id: 'ent-artist', name: 'Artist', price: 1500, qty: 1, desc: '', bold: false, on: false },
]

// Custom expenses — pass-through costs added per service (and overall on review).
const EXPENSE_KEYS = ['Catering', 'Snacks & Stalls', 'Decoration', 'Photography', 'Entertainment', 'Cake', 'Return Gifts', 'overall']
const expensesDefault = () => ({
  Catering: [
    { id: uid('exp'), name: 'Travel', qty: 1, amount: 2000 },
    { id: uid('exp'), name: 'Buffer', qty: 1, amount: 2000 },
    { id: uid('exp'), name: 'Backup Items', qty: 1, amount: 1000 },
    { id: uid('exp'), name: 'Tips', qty: 1, amount: 500 },
  ],
  'Snacks & Stalls': [],
  Decoration: [],
  Photography: [],
  Entertainment: [],
  Cake: [],
  'Return Gifts': [],
  overall: [],
})

const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Mx', 'Dr']
const COUNTRY_CODES = ['+91', '+1', '+44', '+971', '+65', '+61', '+966']
const ROUND_MODES = [
  { v: '5000', label: '₹5K' },
  { v: '1000', label: '₹1K' },
  { v: '500', label: '₹500' },
  { v: 'manual', label: 'Manual' },
]

// Default inclusion lists shown under each Decoration package (fully editable).
const DECOR_BASE = [
  'Backdrop Decor',
  'Entrance Decor',
  'Stage Lights',
  'Stage Carpet',
  'Theme Cutout',
  'Welcome Board',
  '12-Months Baby Board',
  'HBD Neon Light',
  'Cake Table',
  'Cup Cake Props',
  '1" LED',
]
const DECOR_WITHOUT_DEFAULT = [...DECOR_BASE]
const DECOR_WITH_DEFAULT = [
  ...DECOR_BASE,
  'Baby Pathway Cutout (2 Nos)',
  'Entry Carpet',
  'Special Car/Chariot Entry',
  'Fog Entry',
  'Cold Pyros',
]
const DECOR_WITH_BOLD = new Set([
  'Baby Pathway Cutout (2 Nos)',
  'Entry Carpet',
  'Special Car/Chariot Entry',
  'Fog Entry',
  'Cold Pyros',
])
const decorInclusionsDefault = () => ({
  with: DECOR_WITH_DEFAULT.map((t) => ({ text: t, bold: DECOR_WITH_BOLD.has(t) })),
  without: DECOR_WITHOUT_DEFAULT.map((t) => ({ text: t, bold: false })),
})

// Photography — 3 editable packages (name, inclusion lines, price all editable).
// Prices are indicative defaults and can be changed inline.
// items: { text, section, bold } — section = heading (e.g. "Complimentary"); bold = emphasised.
const I = (text) => ({ text, section: false, bold: false })
const IB = (text) => ({ text, section: false, bold: true }) // bold by default
const S = (text) => ({ text, section: true })
const photoPackagesDefault = () => [
  {
    id: 'photo-p1',
    name: 'Package-1',
    price: 10000,
    items: [IB('Traditional Photography'), I('Unlimited Soft Copies'), I('1 Album'), S('Complimentary'), I('1 Photo Frame')],
  },
  {
    id: 'photo-p2',
    name: 'Package-2',
    price: 20000,
    items: [
      IB('Traditional Photography'),
      IB('Traditional Videography'),
      I('Unlimited Soft Copies'),
      I('1 Album'),
      I('1 Traditional Video'),
      I('1 Montage Video'),
      S('Complimentary'),
      I('1 Photo Frame'),
      I('1 Calendar'),
      I('1 Pendrive'),
    ],
  },
  {
    id: 'photo-p3',
    name: 'Package-3',
    price: 20000,
    items: [IB('Traditional Photography'), IB('Candid Photography'), I('Unlimited Soft Copies'), I('1 Album'), S('Complimentary'), I('1 Photo Frame')],
  },
]

const defaultCharges = () => ({
  profitPct: 20,
})

const expenseTotal = (e) => Math.round(toNum(e.qty ?? 1) * toNum(e.amount))
const expensesSum = (expenses = []) => expenses.reduce((s, e) => s + expenseTotal(e), 0)

// Pure per-service total — items→profit%→expenses (round-off is applied once
// to the grand total on the review page, not per service). Pass-through
// expenses are added at cost (no markup) and roll into the total & cost.
const computeService = (lines, charges, expenses = []) => {
  const c = charges || defaultCharges()
  const subtotal = lines.reduce((s, l) => s + (Number(l.total) || 0), 0)
  const expenses_ = expensesSum(expenses)
  const preProfit = subtotal
  const profit = Math.round((preProfit * toNum(c.profitPct)) / 100)
  const total = preProfit + profit + expenses_
  const cost = subtotal + expenses_
  const totalProfit = total - cost
  const totalProfitPct = cost > 0 ? Math.round((totalProfit / cost) * 1000) / 10 : 0
  return { subtotal, expenses: expenses_, profit, beforeRound: total, roundOff: 0, total, cost, totalProfit, totalProfitPct }
}

// Grand-total round-off applied once on the review page.
const computeFinalRound = (raw, mode, manual) => {
  if (mode === 'none') return 0
  if (mode === 'manual') return toNum(manual)
  const step = Number(mode || '1000')
  if (step && raw > 0) {
    const target = Math.max(step, Math.round(raw / step) * step)
    return target - raw
  }
  return 0
}

// ─── small UI primitives ─────────────────────────────────────────────────────
const Field = ({ label, required, error, children, hint }) => (
  <label className="block">
    <div className="text-[11px] font-semibold mb-1 uppercase tracking-wide">
      <span className={error ? 'text-rose-600' : 'text-slate-500'}>{label}</span>
      {required && <span className="text-rose-500 ml-0.5">*</span>}
    </div>
    {children}
    {error ? (
      <div className="text-[11px] text-rose-600 mt-1 font-medium">Required</div>
    ) : hint ? (
      <div className="text-[11px] text-slate-400 mt-1">{hint}</div>
    ) : null}
  </label>
)

const GuestStepper = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={() => onChange(Math.max(0, Number(value) - 10))}
      className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 text-xl font-bold active:bg-slate-200"
    >
      −
    </button>
    <input
      type="number"
      inputMode="numeric"
      min="0"
      className="qv2-input text-center font-bold text-lg flex-1"
      placeholder="0"
      value={showNum(value)}
      onChange={(e) => onChange(toNum(e.target.value))}
    />
    <button
      type="button"
      onClick={() => onChange(Number(value) + 10)}
      className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 text-xl font-bold active:bg-slate-200"
    >
      +
    </button>
  </div>
)

// A compact editable ₹ input with a leading symbol
const MoneyInput = ({ value, onChange, className = '' }) => (
  <div className={`relative ${className}`}>
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
    <input
      type="number"
      inputMode="numeric"
      min="0"
      className="qv2-input"
      style={{ paddingLeft: '1.5rem' }}
      placeholder="0"
      value={showNum(value)}
      onChange={(e) => onChange(toNum(e.target.value))}
    />
  </div>
)

// Per-service profit % — compact, always visible. (Round-off lives once on the
// review page, applied to the grand total.)
const ChargesEditor = ({ charges, onChange }) => {
  const c = charges || defaultCharges()
  const patch = (p) => onChange({ ...c, ...p })
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <span className="text-[13px] font-bold text-slate-700 flex items-center gap-2 flex-1">
          <i className="fas fa-percent text-[#1abc9c]" /> Profit
        </span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            inputMode="numeric"
            className="qv2-input text-center font-bold"
            style={{ width: '72px' }}
            placeholder="0"
            value={showNum(c.profitPct)}
            onChange={(e) => patch({ profitPct: toNum(e.target.value) })}
          />
          <span className="text-[13px] font-semibold text-slate-500">% profit</span>
        </div>
      </div>
    </div>
  )
}

// Custom expenses editor — {id,name,qty,amount}. qty column only when withQty.
const ExpenseEditor = ({ items, setItems, withQty = false, title = 'Other Expenses' }) => {
  const add = () => setItems([...(items || []), { id: uid('exp'), name: '', qty: 1, amount: 0 }])
  const patch = (id, p) => setItems(items.map((it) => (it.id === id ? { ...it, ...p } : it)))
  const remove = (id) => setItems(items.filter((it) => it.id !== id))
  const total = (items || []).reduce((s, e) => s + Math.round(toNum(e.qty ?? 1) * toNum(e.amount)), 0)
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-bold text-slate-700 flex items-center gap-2">
          <i className="fas fa-receipt text-[#1abc9c]" /> {title}
        </span>
        {total > 0 && <span className="text-[12px] font-bold text-slate-500">{fmtINR(total)}</span>}
      </div>
      <div className="space-y-2">
        {(items || []).map((it) => (
          <div key={it.id} className="flex items-end gap-2">
            <div className="flex-1">
              <input className="qv2-input" style={{ fontSize: '13px' }} placeholder="Expense name" value={it.name} onChange={(e) => patch(it.id, { name: e.target.value })} />
            </div>
            {withQty && (
              <div className="w-16">
                <input type="number" inputMode="numeric" min="0" className="qv2-input text-center" style={{ fontSize: '13px' }} placeholder="Qty" value={showNum(it.qty)} onChange={(e) => patch(it.id, { qty: toNum(e.target.value) })} />
              </div>
            )}
            <div className="w-24">
              <MoneyInput value={it.amount} onChange={(v) => patch(it.id, { amount: v })} />
            </div>
            <button type="button" onClick={() => remove(it.id)} className="w-10 h-10 shrink-0 rounded-xl text-rose-500 bg-rose-50 active:bg-rose-100" aria-label="Remove expense">
              <i className="fas fa-trash-can text-[13px]" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-2 text-[12px] font-semibold text-[#0f766e] flex items-center gap-1.5">
        <i className="fas fa-plus text-[10px]" /> Add expense
      </button>
    </div>
  )
}

// Generic editable custom-item list ({id,name,qty,price,desc?}) for Decoration / Cake / Gifts
const CustomItems = ({ items, setItems, addLabel = 'Add custom item', namePlaceholder = 'Item name', withDescription = false }) => {
  const add = () => setItems([...items, { id: uid(), name: '', qty: 1, price: 0, desc: '' }])
  const patch = (id, p) => setItems(items.map((it) => (it.id === id ? { ...it, ...p } : it)))
  const remove = (id) => setItems(items.filter((it) => it.id !== id))
  return (
    <div className="space-y-2.5">
      {items.map((it) => {
        const lineTotal = Math.round(toNum(it.qty) * toNum(it.price))
        return (
          <div key={it.id} className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                className="qv2-input"
                placeholder={namePlaceholder}
                value={it.name}
                onChange={(e) => patch(it.id, { name: e.target.value })}
              />
              <button
                type="button"
                onClick={() => remove(it.id)}
                className="w-10 h-10 shrink-0 rounded-xl text-rose-500 bg-rose-50 active:bg-rose-100"
                aria-label="Remove item"
              >
                <i className="fas fa-trash-can text-[13px]" />
              </button>
            </div>
            {withDescription && (
              <input
                className="qv2-input"
                style={{ fontSize: '13px' }}
                placeholder="Description (optional)"
                value={it.desc || ''}
                onChange={(e) => patch(it.id, { desc: e.target.value })}
              />
            )}
            <div className="flex items-end gap-2">
              <div className="w-20">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Qty</div>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="qv2-input text-center"
                  value={showNum(it.qty)}
                  onChange={(e) => patch(it.id, { qty: toNum(e.target.value) })}
                />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Unit Price</div>
                <MoneyInput value={it.price} onChange={(v) => patch(it.id, { price: v })} />
              </div>
              <div className="text-right pb-2.5 min-w-[76px]">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Total</div>
                <div className="font-extrabold text-[#16a085]">{fmtINR(lineTotal)}</div>
              </div>
            </div>
          </div>
        )
      })}
      <button
        type="button"
        onClick={add}
        className="w-full h-12 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 font-semibold text-[13px] active:bg-slate-50 flex items-center justify-center gap-2"
      >
        <i className="fas fa-plus" /> {addLabel}
      </button>
    </div>
  )
}

// localStorage namespace — kept separate from v1 (/quote) so the two never mix.
const V2_INDEX_KEY = 'aura_v2_quotes_index'
const v2ItemKey = (qn) => `aura_v2_quote_${qn}`

// ─── main ────────────────────────────────────────────────────────────────────
const QuoteV2 = () => {
  const [quoteNo, setQuoteNo] = useState(generateQuoteNo)
  const [stepIndex, setStepIndex] = useState(0)
  const [showErrors, setShowErrors] = useState(false)
  const [printToken, setPrintToken] = useState(0)
  const [pdfMode, setPdfMode] = useState('client') // 'client' (no prices) | 'detailed'
  const scrollRef = useRef(null)
  const printRef = useRef(null)
  const [downloading, setDownloading] = useState(false)

  const [event, setEvent] = useState({
    quoteType: 'customer', // 'customer' | 'package'
    packageName: '',
    salutation: 'Mr',
    guestName: '',
    contactCode: '+91',
    contact: '',
    eventType: 'Birthday',
    date: todayISO(),
    startTime: '18:00',
    endTime: '22:00',
    location: '',
  })

  const [services, setServices] = useState({
    Catering: true,
    'Snacks & Stalls': false,
    Decoration: false,
    Photography: false,
    Entertainment: false,
    Cake: false,
    'Return Gifts': false,
  })

  // Guest count for Snacks & Stalls.
  const [guests, setGuests] = useState(100)

  // ── Catering — split into Non-Veg (the current menu) + Veg, each with its own
  //    guest count & independent selection from the same dish catalog. ──
  const [catCharges, setCatCharges] = useState(defaultCharges)
  // Non-inclusive catering items are bold by default.
  const catDefaultOverride = (id) => {
    const item = CATERING_ITEMS.find((i) => i.id === id)
    return item && item.category !== 'Inclusive' ? { bold: true } : {}
  }
  // Default Non-Veg selection: Chicken Biriyani.
  const initNonVegSelection = () => ({ 'biriyani-chicken': catDefaultOverride('biriyani-chicken') })
  // Default Common selection: sides + service (idiyappam & kheer off by default);
  // Bread Halwa, Onion Raita, Brinjal Dalcha are bold by default.
  const initCommonSelection = () =>
    Object.fromEntries(
      CAT_COMMON_ITEMS.filter((i) => COMMON_DEFAULT_ON.has(i.id)).map((i) => [i.id, COMMON_BOLD.has(i.id) ? { bold: true } : catDefaultOverride(i.id)]),
    )
  // Non-Veg section
  const [nonVegOn, setNonVegOn] = useState(true)
  const [nonVegGuests, setNonVegGuests] = useState(100)
  const [catSelected, setCatSelected] = useState(initNonVegSelection)
  const [catCustom, setCatCustom] = useState([])
  const [catEditing, setCatEditing] = useState({})
  // Veg section
  const [vegOn, setVegOn] = useState(false)
  const [vegGuests, setVegGuests] = useState(100)
  const [vegSelected, setVegSelected] = useState({})
  const [vegCustom, setVegCustom] = useState([])
  const [vegEditing, setVegEditing] = useState({})
  // Common section (inclusive + service, priced at total catering guests)
  const [commonSelected, setCommonSelected] = useState(initCommonSelection)
  const [commonCustom, setCommonCustom] = useState([])
  const [commonEditing, setCommonEditing] = useState({})

  // ── Snacks & Stalls ──
  const [snackSelected, setSnackSelected] = useState({})
  const [snackCustom, setSnackCustom] = useState([])
  const [snackFilter, setSnackFilter] = useState('All')
  const [snackEditing, setSnackEditing] = useState({})
  const [snackCharges, setSnackCharges] = useState(defaultCharges)

  // ── Decoration ──
  const [decorGender, setDecorGender] = useState('') // Boy | Girl (Birthday only)
  const [decorBase, setDecorBase] = useState('with') // 'with' | 'without' | null
  const [decorPrices, setDecorPrices] = useState({ with: 20000, without: 16000 })
  const [decorInclusions, setDecorInclusions] = useState(decorInclusionsDefault)
  const [decorCustom, setDecorCustom] = useState([])
  const [decorCharges, setDecorCharges] = useState(() => ({ ...defaultCharges(), profitPct: 25 }))

  // ── Photography ──
  const [photoPackages, setPhotoPackages] = useState(photoPackagesDefault) // editable packages
  const [photoSelected, setPhotoSelected] = useState({ 'photo-p1': true }) // {id: true}
  const [photoCustom, setPhotoCustom] = useState([])
  const [photoCharges, setPhotoCharges] = useState(defaultCharges)

  // ── Entertainment ──
  const [entOptions, setEntOptions] = useState(entDefault)
  const [entCharges, setEntCharges] = useState(defaultCharges)

  // ── Cake ──
  const [cakeOn, setCakeOn] = useState(true)
  const [cakePrice, setCakePrice] = useState(6000)
  const [cakeDesc, setCakeDesc] = useState('')
  const [cakeCustom, setCakeCustom] = useState([])
  const [cakeCharges, setCakeCharges] = useState(defaultCharges)

  // ── Return Gifts ──
  const [giftCustom, setGiftCustom] = useState([])
  const [giftCharges, setGiftCharges] = useState(defaultCharges)

  // ── Custom expenses (per-service + quote-level "overall") ──
  const [expenses, setExpenses] = useState(expensesDefault)
  const setServiceExpenses = (key, list) => setExpenses((e) => ({ ...e, [key]: list }))

  // ── Final round-off (applied once to the grand total on the review page) ──
  const [finalRound, setFinalRound] = useState({ mode: '1000', manual: 0 })

  // ── Saved quotes (localStorage) ──
  const [savedQuotes, setSavedQuotes] = useState([])
  const [savedOpen, setSavedOpen] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [justSaved, setJustSaved] = useState(false)

  const isBirthday = event.eventType === 'Birthday'

  // Neutralise body transform (index.css) so fixed footer resolves to viewport.
  useEffect(() => {
    const body = document.body
    const prev = { transform: body.style.transform, perspective: body.style.perspective }
    body.style.transform = 'none'
    body.style.perspective = 'none'
    return () => {
      body.style.transform = prev.transform
      body.style.perspective = prev.perspective
    }
  }, [])

  // ── Steps: Details + selected services (in SERVICES order) + Review ──
  const selectedServices = SERVICES.filter((s) => services[s.key]).map((s) => s.key)
  const steps = ['Details', ...selectedServices, 'Review']
  const currentStep = steps[Math.min(stepIndex, steps.length - 1)]

  // Clamp step index if services change
  useEffect(() => {
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length])

  // Scroll to top on step change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'auto' })
    window.scrollTo({ top: 0 })
  }, [stepIndex])

  // ── validation (mirrors v1) ──
  const contactDigits = (event.contact || '').replace(/\D/g, '')
  const isIndiaCode = (event.contactCode || '+91') === '+91'
  const phoneMin = isIndiaCode ? 10 : 7
  const phoneMax = isIndiaCode ? 10 : 15
  const isPackageQuote = event.quoteType === 'package'
  const detailErrors = isPackageQuote
    ? {
        // Package quotes need only a package name + at least one service.
        packageName: !event.packageName?.trim(),
        services: selectedServices.length === 0,
      }
    : {
        guestName: !event.guestName?.trim(),
        contact:
          !event.contact?.trim() || contactDigits.length < phoneMin || contactDigits.length > phoneMax,
        eventType: !event.eventType?.trim(),
        location: !event.location?.trim(),
        services: selectedServices.length === 0,
      }
  const detailHasErrors = Object.values(detailErrors).some(Boolean)

  const stepHasErrors = (step) => {
    if (step === 'Details') return detailHasErrors
    if (step === 'Catering') return (!nonVegOn && !vegOn) || cateringGuests <= 0
    if (step === 'Snacks & Stalls') return !(guests > 0)
    return false
  }

  // description shown on a menu line = per-line override, else the item default
  const lineDesc = (item, ov) => (ov.description !== undefined ? ov.description : item.description || '')

  // ── Catering pricing — Non-Veg + Veg (food) + Common (inclusive/service) ──
  const cateringGuests = (nonVegOn ? Number(nonVegGuests) || 0 : 0) + (vegOn ? Number(vegGuests) || 0 : 0)
  const nvFoodItems = useMemo(() => [...NONVEG_CATALOG, ...catCustom], [catCustom])
  const vgFoodItems = useMemo(() => [...VEG_CATALOG, ...vegCustom], [vegCustom])
  const commonItems = useMemo(() => [...CAT_COMMON_ITEMS, ...commonCustom], [commonCustom])
  const buildCatLines = (items, sel, g, veg) =>
    items
      .filter((i) => sel[i.id] !== undefined)
      .map((item) => {
        const ov = sel[item.id] || {}
        return { item, override: ov, description: lineDesc(item, ov), label: ov.label, bold: ov.bold, veg, ...calcLine(item, g, ov, { packageName: 'Starter' }) }
      })
  const catLines = useMemo(
    () => [
      ...(nonVegOn ? buildCatLines(nvFoodItems, catSelected, nonVegGuests, false) : []),
      ...(vegOn ? buildCatLines(vgFoodItems, vegSelected, vegGuests, true) : []),
      ...buildCatLines(commonItems, commonSelected, cateringGuests || 100, null),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nvFoodItems, vgFoodItems, commonItems, catSelected, vegSelected, commonSelected, nonVegOn, vegOn, nonVegGuests, vegGuests, cateringGuests],
  )

  // ── Snacks & Stalls pricing (reuse calcLine) ──
  const snackAllItems = useMemo(() => [...SNACKS_ITEMS, ...snackCustom], [snackCustom])
  const snackLines = useMemo(
    () =>
      snackAllItems
        .filter((i) => snackSelected[i.id] !== undefined)
        .map((item) => {
          const ov = snackSelected[item.id] || {}
          return { item, override: ov, description: lineDesc(item, ov), label: ov.label, bold: ov.bold, ...calcLine(item, guests, ov, { packageName: 'Custom' }) }
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snackAllItems, snackSelected, guests],
  )

  // ── Decoration lines ──
  const decorLines = useMemo(() => {
    const out = []
    if (decorBase === 'with')
      out.push({ name: 'Decoration with Entry', total: toNum(decorPrices.with), items: decorInclusions.with })
    else if (decorBase === 'without')
      out.push({ name: 'Decoration', total: toNum(decorPrices.without), items: decorInclusions.without })
    decorCustom.forEach((it) =>
      out.push({ name: it.name || 'Custom', qty: toNum(it.qty), price: toNum(it.price), total: Math.round(toNum(it.qty) * toNum(it.price)) }),
    )
    return out
  }, [decorBase, decorPrices, decorInclusions, decorCustom])

  // ── Photography lines ──
  const photoLines = useMemo(() => {
    const out = []
    photoPackages.forEach((p) => {
      if (photoSelected[p.id]) out.push({ name: p.name, items: p.items, total: toNum(p.price) })
    })
    photoCustom.forEach((it) =>
      out.push({ name: it.name || 'Custom', qty: toNum(it.qty), price: toNum(it.price), total: Math.round(toNum(it.qty) * toNum(it.price)) }),
    )
    return out
  }, [photoPackages, photoSelected, photoCustom])

  // ── Entertainment lines ──
  const entLines = useMemo(
    () =>
      entOptions
        .filter((o) => o.on)
        .map((o) => ({
          name: o.name || 'Entertainment',
          description: o.desc || '',
          bold: o.bold,
          qty: toNum(o.qty),
          price: toNum(o.price),
          total: Math.round(toNum(o.qty) * toNum(o.price)),
        })),
    [entOptions],
  )

  // ── Cake lines ──
  const cakeLines = useMemo(() => {
    const out = []
    if (cakeOn) out.push({ name: 'Birthday Cake', description: cakeDesc, total: toNum(cakePrice) })
    cakeCustom.forEach((it) =>
      out.push({ name: it.name || 'Custom', description: it.desc || '', qty: toNum(it.qty), price: toNum(it.price), total: Math.round(toNum(it.qty) * toNum(it.price)) }),
    )
    return out
  }, [cakeOn, cakePrice, cakeDesc, cakeCustom])

  // ── Return gift lines ──
  const giftLines = useMemo(
    () =>
      giftCustom.map((it) => ({
        name: it.name || 'Return Gift',
        description: it.desc || '',
        qty: toNum(it.qty),
        price: toNum(it.price),
        total: Math.round(toNum(it.qty) * toNum(it.price)),
      })),
    [giftCustom],
  )

  // ── Per-service computed totals (expenses roll into each service) ──
  const serviceTotals = {
    Catering: computeService(catLines, catCharges, expenses.Catering),
    'Snacks & Stalls': computeService(snackLines, snackCharges, expenses['Snacks & Stalls']),
    Decoration: computeService(decorLines, decorCharges, expenses.Decoration),
    Photography: computeService(photoLines, photoCharges, expenses.Photography),
    Entertainment: computeService(entLines, entCharges, expenses.Entertainment),
    Cake: computeService(cakeLines, cakeCharges, expenses.Cake),
    'Return Gifts': computeService(giftLines, giftCharges, expenses['Return Gifts']),
  }
  // Per-plate rate for catering (total ÷ guests)
  const cateringPerPlate = cateringGuests > 0 ? Math.round(serviceTotals.Catering.total / cateringGuests) : 0
  // Overall (quote-level) expenses — pass-through cost added to the grand total.
  const overallExpensesTotal = expensesSum(expenses.overall)
  const rawGrandTotal = selectedServices.reduce((s, k) => s + serviceTotals[k].total, 0) + overallExpensesTotal
  // Round-off applied once, here, to the grand total (not per service).
  const finalRoundOff = computeFinalRound(rawGrandTotal, finalRound.mode, finalRound.manual)
  const grandTotal = rawGrandTotal + finalRoundOff
  const grandProfit = selectedServices.reduce((s, k) => s + serviceTotals[k].totalProfit, 0) + finalRoundOff
  const grandPreProfit =
    selectedServices.reduce((s, k) => s + serviceTotals[k].cost, 0) + overallExpensesTotal
  const grandProfitPct =
    grandPreProfit > 0 ? Math.round((grandProfit / grandPreProfit) * 1000) / 10 : 0

  // ── Saved quotes: load index on mount ──
  useEffect(() => {
    try {
      const idx = JSON.parse(localStorage.getItem(V2_INDEX_KEY) || '[]')
      if (Array.isArray(idx)) setSavedQuotes(idx)
    } catch {}
  }, [])

  // Full snapshot of everything needed to restore a quote.
  const snapshot = useMemo(
    () => ({
      quoteNo,
      event,
      services,
      guests,
      nonVegOn,
      nonVegGuests,
      catSelected,
      catCustom,
      vegOn,
      vegGuests,
      vegSelected,
      vegCustom,
      commonSelected,
      commonCustom,
      catCharges,
      snackSelected,
      snackCustom,
      snackCharges,
      decorGender,
      decorBase,
      decorPrices,
      decorInclusions,
      decorCustom,
      decorCharges,
      photoPackages,
      photoSelected,
      photoCustom,
      photoCharges,
      entOptions,
      entCharges,
      cakeOn,
      cakePrice,
      cakeDesc,
      cakeCustom,
      cakeCharges,
      giftCustom,
      giftCharges,
      expenses,
      finalRound,
    }),
    [quoteNo, event, services, guests, nonVegOn, nonVegGuests, catSelected, catCustom, vegOn, vegGuests, vegSelected, vegCustom, commonSelected, commonCustom, catCharges, snackSelected, snackCustom, snackCharges, decorGender, decorBase, decorPrices, decorInclusions, decorCustom, decorCharges, photoPackages, photoSelected, photoCustom, photoCharges, entOptions, entCharges, cakeOn, cakePrice, cakeDesc, cakeCustom, cakeCharges, giftCustom, giftCharges, expenses, finalRound],
  )

  // Autosave (debounced) — starts once a customer name (or package name) is entered.
  useEffect(() => {
    const pkg = event.quoteType === 'package'
    if (pkg ? !event.packageName?.trim() : !event.guestName.trim()) return
    const t = setTimeout(() => {
      try {
        const savedAt = new Date().toISOString()
        localStorage.setItem(v2ItemKey(quoteNo), JSON.stringify({ ...snapshot, grandTotal, savedAt }))
        const summary = {
          quoteNo,
          savedAt,
          quoteType: event.quoteType,
          customerName: pkg
            ? event.packageName
            : [event.salutation, event.guestName].filter(Boolean).join(' ') || event.guestName,
          contact: pkg ? '' : `${event.contactCode} ${event.contact}`.trim(),
          eventType: pkg ? 'Package' : event.eventType,
          date: pkg ? '' : event.date,
          services: selectedServices,
          grandTotal,
        }
        setSavedQuotes((prev) => {
          const next = [summary, ...prev.filter((q) => q.quoteNo !== quoteNo)]
          localStorage.setItem(V2_INDEX_KEY, JSON.stringify(next))
          return next
        })
        setJustSaved(true)
        setTimeout(() => setJustSaved(false), 1500)
      } catch {}
    }, 600)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, grandTotal])

  const applyState = (s) => {
    if (!s) return
    if (s.quoteNo) setQuoteNo(s.quoteNo)
    if (s.event) setEvent({ quoteType: 'customer', packageName: '', ...s.event })
    if (s.services) setServices(s.services)
    setGuests(s.guests ?? s.catGuests ?? 100)
    setNonVegOn(s.nonVegOn ?? true)
    setNonVegGuests(s.nonVegGuests ?? s.guests ?? s.catGuests ?? 100)
    setCatSelected(s.catSelected ?? {})
    setCatCustom(s.catCustom ?? [])
    setVegOn(s.vegOn ?? false)
    setVegGuests(s.vegGuests ?? 100)
    setVegSelected(s.vegSelected ?? {})
    setVegCustom(s.vegCustom ?? [])
    setCommonSelected(s.commonSelected ?? initCommonSelection())
    setCommonCustom(s.commonCustom ?? [])
    setCatCharges(s.catCharges ?? defaultCharges())
    setSnackSelected(s.snackSelected ?? {})
    setSnackCustom(s.snackCustom ?? [])
    setSnackCharges(s.snackCharges ?? defaultCharges())
    setDecorGender(s.decorGender ?? '')
    setDecorBase(s.decorBase ?? 'with')
    setDecorPrices(s.decorPrices ?? { with: 20000, without: 16000 })
    setDecorInclusions(s.decorInclusions ?? decorInclusionsDefault())
    setDecorCustom(s.decorCustom ?? [])
    setDecorCharges(s.decorCharges ?? { ...defaultCharges(), profitPct: 25 })
    setPhotoPackages(s.photoPackages ?? photoPackagesDefault())
    setPhotoSelected(s.photoSelected ?? {})
    setPhotoCustom(s.photoCustom ?? [])
    setPhotoCharges(s.photoCharges ?? defaultCharges())
    setEntOptions(s.entOptions ?? entDefault())
    setEntCharges(s.entCharges ?? defaultCharges())
    setCakeOn(s.cakeOn ?? true)
    setCakePrice(s.cakePrice ?? 6000)
    setCakeDesc(s.cakeDesc ?? '')
    setCakeCustom(s.cakeCustom ?? [])
    setCakeCharges(s.cakeCharges ?? defaultCharges())
    setGiftCustom(s.giftCustom ?? [])
    setGiftCharges(s.giftCharges ?? defaultCharges())
    setExpenses(s.expenses ? { ...expensesDefault(), ...s.expenses } : expensesDefault())
    setFinalRound(s.finalRound ?? { mode: '1000', manual: 0 })
    setSavedOpen(false)
    setStepIndex(0)
    setShowErrors(false)
  }

  const loadQuote = (qn) => {
    try {
      const raw = localStorage.getItem(v2ItemKey(qn))
      if (!raw) return
      applyState(JSON.parse(raw))
    } catch {}
  }

  // Clone an existing quote: keep every service/pricing choice, but start fresh
  // with a new quote number and blank customer details (name, mobile, salutation).
  const cloneQuote = (qn) => {
    try {
      const raw = localStorage.getItem(v2ItemKey(qn))
      if (!raw) return
      const s = JSON.parse(raw)
      applyState({
        ...s,
        quoteNo: generateQuoteNo(),
        event: {
          ...(s.event || {}),
          salutation: 'Mr',
          guestName: '',
          contactCode: s.event?.contactCode || '+91',
          contact: '',
        },
      })
    } catch {}
  }

  // ── Share ──
  const [shareOpen, setShareOpen] = useState(false)
  const [shareToast, setShareToast] = useState('')
  const flashShare = (msg) => {
    setShareToast(msg)
    setTimeout(() => setShareToast(''), 2200)
  }
  const buildShareUrl = () => `${window.location.origin}/quote-v2?q=${encodeShare(snapshot)}`
  const shareText = () => {
    const who = event.quoteType === 'package' ? event.packageName : [event.salutation, event.guestName].filter(Boolean).join(' ')
    return `AuraSquare Events — Quotation${who ? ` for ${who}` : ''} (${fmtINR(grandTotal)})`
  }
  const doCopyLink = async () => {
    const url = buildShareUrl()
    try {
      await navigator.clipboard.writeText(url)
      flashShare('Link copied to clipboard')
    } catch {
      window.prompt('Copy this quote link:', url)
    }
  }
  const doWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText()}\n${buildShareUrl()}`)}`
    window.open(url, '_blank')
  }
  const doNativeShare = async () => {
    const url = buildShareUrl()
    if (navigator.share) {
      try {
        await navigator.share({ title: 'AuraSquare Quotation', text: shareText(), url })
      } catch {}
    } else {
      doCopyLink()
    }
  }

  // Decode a shared quote from the URL (?q=...) on first load.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const q = params.get('q')
      if (!q) return
      const s = decodeShare(q)
      if (s) applyState(s)
      const url = new URL(window.location.href)
      url.searchParams.delete('q')
      window.history.replaceState({}, '', url.toString())
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const deleteQuote = (qn) => {
    try {
      localStorage.removeItem(v2ItemKey(qn))
    } catch {}
    setSavedQuotes((prev) => {
      const next = prev.filter((q) => q.quoteNo !== qn)
      try {
        localStorage.setItem(V2_INDEX_KEY, JSON.stringify(next))
      } catch {}
      return next
    })
  }

  const newQuote = () => {
    setQuoteNo(generateQuoteNo())
    setEvent({ quoteType: 'customer', packageName: '', salutation: 'Mr', guestName: '', contactCode: '+91', contact: '', eventType: 'Birthday', date: todayISO(), startTime: '18:00', endTime: '22:00', location: '' })
    setServices({ Catering: true, 'Snacks & Stalls': false, Decoration: false, Photography: false, Entertainment: false, Cake: false, 'Return Gifts': false })
    setGuests(100)
    setNonVegOn(true)
    setNonVegGuests(100)
    setCatSelected(initNonVegSelection())
    setCatCustom([])
    setVegOn(false)
    setVegGuests(100)
    setVegSelected({})
    setVegCustom([])
    setCommonSelected(initCommonSelection())
    setCommonCustom([])
    setCatCharges(defaultCharges())
    setSnackSelected({})
    setSnackCustom([])
    setSnackCharges(defaultCharges())
    setDecorGender('')
    setDecorBase('with')
    setDecorPrices({ with: 20000, without: 16000 })
    setDecorInclusions(decorInclusionsDefault())
    setDecorCustom([])
    setDecorCharges({ ...defaultCharges(), profitPct: 25 })
    setPhotoPackages(photoPackagesDefault())
    setPhotoSelected({ 'photo-p1': true })
    setPhotoCustom([])
    setPhotoCharges(defaultCharges())
    setEntOptions(entDefault())
    setEntCharges(defaultCharges())
    setCakeOn(true)
    setCakePrice(6000)
    setCakeDesc('')
    setCakeCustom([])
    setCakeCharges(defaultCharges())
    setGiftCustom([])
    setGiftCharges(defaultCharges())
    setExpenses(expensesDefault())
    setFinalRound({ mode: '1000', manual: 0 })
    setSavedOpen(false)
    setStepIndex(0)
    setShowErrors(false)
  }

  // Lock body scroll while the saved drawer is open
  useEffect(() => {
    if (!savedOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [savedOpen])

  // ── navigation ──
  const goNext = () => {
    if (stepHasErrors(currentStep)) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    setStepIndex((i) => Math.min(steps.length - 1, i + 1))
  }
  const goBack = () => {
    setShowErrors(false)
    setStepIndex((i) => Math.max(0, i - 1))
  }
  const goToStep = (i) => {
    if (i < 0 || i > steps.length - 1) return
    setShowErrors(false)
    setStepIndex(i)
  }
  const editService = (key) => goToStep(steps.indexOf(key))

  // ── Catering item helpers ──
  // Generic catering-section handlers, bound per section (Non-Veg / Veg).
  const makeCatHandlers = (setSelected, setCustom, setEditing) => ({
    toggle: (item) =>
      setSelected((s) => {
        const n = { ...s }
        if (n[item.id] !== undefined) delete n[item.id]
        else n[item.id] = item.category !== 'Inclusive' ? { bold: true } : {}
        return n
      }),
    setOverride: (id, key, value) => setSelected((s) => ({ ...s, [id]: { ...(s[id] || {}), [key]: value } })),
    addCustom: () => {
      const it = { id: uid('cat'), name: '', category: 'Custom', unitLabel: 'Nos', rule: 'flat', flatPrice: 0, custom: true, serviceType: 'Catering' }
      setCustom((c) => [...c, it])
      setSelected((s) => ({ ...s, [it.id]: {} }))
      setEditing((e) => ({ ...e, [it.id]: true }))
    },
    updateCustom: (id, key, value) => setCustom((c) => c.map((it) => (it.id === id ? { ...it, [key]: value } : it))),
    removeCustom: (id) => {
      setCustom((c) => c.filter((it) => it.id !== id))
      setSelected((s) => {
        const n = { ...s }
        delete n[id]
        return n
      })
    },
  })
  const nvHandlers = makeCatHandlers(setCatSelected, setCatCustom, setCatEditing)
  const vgHandlers = makeCatHandlers(setVegSelected, setVegCustom, setVegEditing)
  const commonHandlers = makeCatHandlers(setCommonSelected, setCommonCustom, setCommonEditing)

  // ── Snacks & Stalls item helpers ──
  const toggleSnackItem = (item) =>
    setSnackSelected((s) => {
      const n = { ...s }
      if (n[item.id] !== undefined) delete n[item.id]
      else n[item.id] = {}
      return n
    })
  const setSnackOverride = (id, key, value) =>
    setSnackSelected((s) => ({ ...s, [id]: { ...(s[id] || {}), [key]: value } }))
  const addSnackCustom = () => {
    const it = { id: uid('snk'), name: '', category: 'Custom', unitLabel: 'Nos', rule: 'flat', flatPrice: 0, custom: true, serviceType: 'Snacks' }
    setSnackCustom((c) => [...c, it])
    setSnackSelected((s) => ({ ...s, [it.id]: {} }))
    setSnackEditing((e) => ({ ...e, [it.id]: true }))
  }
  const updateSnackCustom = (id, key, value) =>
    setSnackCustom((c) => c.map((it) => (it.id === id ? { ...it, [key]: value } : it)))
  const removeSnackCustom = (id) => {
    setSnackCustom((c) => c.filter((it) => it.id !== id))
    setSnackSelected((s) => {
      const n = { ...s }
      delete n[id]
      return n
    })
  }

  // Single-select: choosing a package clears the others; tapping the selected one clears it.
  const togglePhoto = (id) => setPhotoSelected((s) => (s[id] ? {} : { [id]: true }))
  const updatePhotoPkg = (id, patch) => setPhotoPackages((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)))

  // ── Entertainment helpers ──
  const updateEnt = (id, patch) => setEntOptions((os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o)))
  const toggleEnt = (id) => setEntOptions((os) => os.map((o) => (o.id === id ? { ...o, on: !o.on } : o)))
  const addEntCustom = () => setEntOptions((os) => [...os, { id: uid('ent'), name: '', price: 0, qty: 1, desc: '', bold: false, on: true, custom: true }])
  const removeEnt = (id) => setEntOptions((os) => os.filter((o) => o.id !== id))

  // ── Export helpers ──
  const buildFilename = (ext) => {
    const rawName = event.quoteType === 'package' ? event.packageName || 'Package' : event.guestName || 'Customer'
    const safeName = rawName.toString().trim().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32)
    const datePart = event.quoteType === 'package' ? '' : event.date
    return [pdfMode === 'client' ? 'Quote' : 'Quote-Detailed', datePart, safeName || 'Quote', quoteNo].filter(Boolean).join('_') + '.' + ext
  }
  const captureCanvas = async () => {
    const node = printRef.current
    // Let the "Generating…" mask paint before we move the print node on-screen,
    // so the poster never flashes into view (two rAFs guarantee a paint frame).
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
    // Ensure images (logo) are decoded and fonts ready before capture.
    await Promise.all(
      Array.from(node.querySelectorAll('img')).map((img) => (img.complete ? Promise.resolve() : img.decode().catch(() => {}))),
    )
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready
    } catch {}
    // Primary: html-to-image renders via the browser's own layout engine
    // (SVG <foreignObject>), so inter-word spaces are preserved on every browser
    // — unlike html2canvas which drops them in Firefox.
    // The print node lives off-screen (left:-10000px); bring it to 0,0 during
    // capture so html-to-image doesn't render it outside the canvas.
    const prev = { position: node.style.position, left: node.style.left, top: node.style.top }
    // Temporarily detach cross-origin font stylesheets (Google Fonts) so html-to-image
    // doesn't try to read their cssRules — that throws a benign CORS SecurityError and
    // spams the console. Font Awesome (cdnjs, CORS-enabled) stays so its icon glyphs
    // still embed and render; the poster's text is system-ui, so nothing else is lost.
    const detached = []
    document.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
      if (/fonts\.googleapis\.com|fonts\.gstatic\.com/i.test(l.href || '')) {
        detached.push([l, l.parentNode, l.nextSibling])
        l.parentNode && l.parentNode.removeChild(l)
      }
    })
    const restoreSheets = () => detached.forEach(([l, parent, next]) => parent && parent.insertBefore(l, next))
    try {
      const { toCanvas } = await import('html-to-image')
      node.style.position = 'fixed'
      node.style.left = '0'
      node.style.top = '0'
      const canvas = await toCanvas(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        width: node.scrollWidth,
        height: node.scrollHeight,
      })
      Object.assign(node.style, prev)
      restoreSheets()
      if (!canvas || !canvas.width) throw new Error('empty canvas')
      return canvas
    } catch (e) {
      Object.assign(node.style, prev)
      restoreSheets()
      // Fallback: html2canvas (spaces may collapse on Firefox).
      console.warn('html-to-image failed, falling back to html2canvas', e)
      const html2canvas = (await import('html2canvas')).default
      return html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        imageTimeout: 15000,
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
        onclone: (doc) => {
          doc.querySelectorAll('.qv2-print-area, .qv2-print-area *').forEach((el) => {
            el.style.whiteSpace = 'pre-wrap'
          })
        },
      })
    }
  }

  // ── PDF download: single page sized to A4 width × design height → full-bleed ──
  const handleDownload = async () => {
    if (!printRef.current || downloading) return
    setDownloading(true)
    try {
      const { jsPDF } = await import('jspdf')
      const canvas = await captureCanvas()
      const pageW = 595.28 // A4 width in pt
      const pageH = (canvas.height * pageW) / canvas.width
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [pageW, pageH] })
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, pageW, pageH, undefined, 'FAST')
      // Overlay clickable link annotations on the rasterised links (website, insta).
      const node = printRef.current
      const nodeRect = node.getBoundingClientRect()
      const scale = pageW / nodeRect.width
      node.querySelectorAll('[data-pdflink]').forEach((el) => {
        const url = el.getAttribute('data-pdflink')
        const r = el.getBoundingClientRect()
        pdf.link((r.left - nodeRect.left) * scale, (r.top - nodeRect.top) * scale, r.width * scale, r.height * scale, { url })
      })
      pdf.save(buildFilename('pdf'))
    } catch (e) {
      console.error('PDF export failed', e)
    }
    setDownloading(false)
  }

  // ── PNG download ──
  const handleDownloadImage = async () => {
    if (!printRef.current || downloading) return
    setDownloading(true)
    try {
      const canvas = await captureCanvas()
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = buildFilename('png')
      link.href = url
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 2000)
    } catch (e) {
      console.error('Image export failed', e)
    }
    setDownloading(false)
  }

  // ── print (legacy fallback, unused) ──
  useEffect(() => {
    if (printToken === 0) return
    const originalTitle = document.title
    const safeName = (event.guestName || 'Customer')
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32)
    document.title = [pdfMode === 'client' ? 'Quote' : 'Quote-Detailed', event.date, safeName || 'Quote', quoteNo].filter(Boolean).join('_')
    const restore = () => {
      document.title = originalTitle
      window.removeEventListener('afterprint', restore)
    }
    window.addEventListener('afterprint', restore)
    const t = setTimeout(restore, 6000)
    window.print()
    return () => clearTimeout(t)
  }, [printToken]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── render helpers ──
  const stepNumber = stepIndex + 1
  const progressPct = (stepNumber / steps.length) * 100

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <GlobalStylesV2 />

      {/* App chrome — hidden when printing */}
      <div className="qv2-no-print">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
          <div className="max-w-[560px] mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Link to="/" className="shrink-0">
                <img src="/assets/images/logo.png" alt="AuraSquare" className="h-8 w-auto" onError={(e) => (e.currentTarget.style.display = 'none')} />
              </Link>
              <div>
                <div className="font-extrabold text-[15px] leading-none">Quote Builder <span className="text-[#1abc9c]">v2</span></div>
                <div className="text-[10.5px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                  {quoteNo}
                  {justSaved && <span className="text-[#1abc9c] font-semibold"><i className="fas fa-check text-[9px]" /> Saved</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="h-9 w-9 rounded-xl border border-slate-200 text-slate-600 flex items-center justify-center active:bg-slate-50"
                aria-label="Share quote"
              >
                <i className="fas fa-share-nodes text-[13px]" />
              </button>
              <button
                type="button"
                onClick={() => setSavedOpen(true)}
                className="relative h-9 px-3 rounded-xl border border-slate-200 text-slate-600 text-[12px] font-semibold flex items-center gap-1.5 active:bg-slate-50"
              >
                <i className="fas fa-folder-open text-[12px]" /> Saved
                {savedQuotes.length > 0 && (
                  <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1abc9c] text-white text-[10px] font-bold flex items-center justify-center">
                    {savedQuotes.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={newQuote}
                className="h-9 px-3 rounded-xl bg-slate-900 text-white text-[12px] font-semibold flex items-center gap-1.5 active:opacity-90"
              >
                <i className="fas fa-plus text-[11px]" /> New
              </button>
            </div>
          </div>

          {/* Progress + tappable step menu */}
          <div className="max-w-[560px] mx-auto px-4 pb-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[12px] font-bold text-slate-700">{currentStep}</span>
              <span className="text-[11px] text-slate-400 font-semibold">Step {stepNumber} of {steps.length}</span>
            </div>
            <div className="flex gap-1 mb-2">
              {steps.map((s, i) => (
                <button
                  key={s + i}
                  type="button"
                  onClick={() => goToStep(i)}
                  className="flex-1 h-1.5 rounded-full transition-colors"
                  style={{ background: i <= stepIndex ? '#1abc9c' : '#e2e8f0' }}
                  aria-label={s}
                />
              ))}
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1">
              {steps.map((s, i) => (
                <button
                  key={s + i}
                  type="button"
                  onClick={() => goToStep(i)}
                  className={`shrink-0 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition ${
                    i === stepIndex
                      ? 'bg-[#1abc9c] text-white border-[#1abc9c]'
                      : i < stepIndex
                        ? 'bg-white text-[#0f766e] border-[#1abc9c]/40'
                        : 'bg-white text-slate-500 border-slate-200'
                  }`}
                >
                  {i < stepIndex && <i className="fas fa-check text-[9px] mr-1" />}
                  {s}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* Content */}
        <main ref={scrollRef} className="max-w-[560px] mx-auto px-4 pt-4" style={{ paddingBottom: '120px' }}>
          {currentStep === 'Details' && (
            <DetailsStep
              event={event}
              setEvent={setEvent}
              services={services}
              setServices={setServices}
              errors={detailErrors}
              showErrors={showErrors}
              isIndiaCode={isIndiaCode}
              phoneMin={phoneMin}
              phoneMax={phoneMax}
              contactDigits={contactDigits}
            />
          )}

          {currentStep === 'Catering' && (
            <CateringStep
              nv={{ on: nonVegOn, setOn: setNonVegOn, guests: nonVegGuests, setGuests: setNonVegGuests, selected: catSelected, custom: catCustom, editing: catEditing, setEditing: setCatEditing, h: nvHandlers }}
              vg={{ on: vegOn, setOn: setVegOn, guests: vegGuests, setGuests: setVegGuests, selected: vegSelected, custom: vegCustom, editing: vegEditing, setEditing: setVegEditing, h: vgHandlers }}
              common={{ selected: commonSelected, custom: commonCustom, editing: commonEditing, setEditing: setCommonEditing, h: commonHandlers }}
              cateringGuests={cateringGuests}
              perPlate={cateringPerPlate}
              charges={catCharges}
              setCharges={setCatCharges}
              totals={serviceTotals.Catering}
              showErrors={showErrors}
              expenses={expenses.Catering}
              setExpenses={(list) => setServiceExpenses('Catering', list)}
            />
          )}

          {currentStep === 'Snacks & Stalls' && (
            <MenuStep
              baseItems={SNACKS_ITEMS}
              cats={SNACKS_CATS}
              addLabel="Add custom snack / stall"
              guests={guests}
              setGuests={setGuests}
              selected={snackSelected}
              filter={snackFilter}
              setFilter={setSnackFilter}
              editing={snackEditing}
              setEditing={setSnackEditing}
              onToggle={toggleSnackItem}
              setOverride={setSnackOverride}
              custom={snackCustom}
              addCustom={addSnackCustom}
              updateCustom={updateSnackCustom}
              removeCustom={removeSnackCustom}
              charges={snackCharges}
              setCharges={setSnackCharges}
              totals={serviceTotals['Snacks & Stalls']}
              showErrors={showErrors}
              expenses={expenses['Snacks & Stalls']}
              setExpenses={(list) => setServiceExpenses('Snacks & Stalls', list)}
            />
          )}

          {currentStep === 'Decoration' && (
            <DecorationStep
              isBirthday={isBirthday}
              gender={decorGender}
              setGender={setDecorGender}
              base={decorBase}
              setBase={setDecorBase}
              prices={decorPrices}
              setPrices={setDecorPrices}
              inclusions={decorInclusions}
              setInclusions={setDecorInclusions}
              custom={decorCustom}
              setCustom={setDecorCustom}
              charges={decorCharges}
              setCharges={setDecorCharges}
              totals={serviceTotals.Decoration}
              expenses={expenses.Decoration}
              setExpenses={(list) => setServiceExpenses('Decoration', list)}
            />
          )}

          {currentStep === 'Photography' && (
            <PhotographyStep
              packages={photoPackages}
              selected={photoSelected}
              onToggle={togglePhoto}
              updatePkg={updatePhotoPkg}
              custom={photoCustom}
              setCustom={setPhotoCustom}
              charges={photoCharges}
              setCharges={setPhotoCharges}
              totals={serviceTotals.Photography}
              expenses={expenses.Photography}
              setExpenses={(list) => setServiceExpenses('Photography', list)}
            />
          )}

          {currentStep === 'Entertainment' && (
            <EntertainmentStep
              options={entOptions}
              onToggle={toggleEnt}
              update={updateEnt}
              addCustom={addEntCustom}
              remove={removeEnt}
              charges={entCharges}
              setCharges={setEntCharges}
              totals={serviceTotals.Entertainment}
              expenses={expenses.Entertainment}
              setExpenses={(list) => setServiceExpenses('Entertainment', list)}
            />
          )}

          {currentStep === 'Cake' && (
            <CakeStep
              on={cakeOn}
              setOn={setCakeOn}
              price={cakePrice}
              setPrice={setCakePrice}
              desc={cakeDesc}
              setDesc={setCakeDesc}
              custom={cakeCustom}
              setCustom={setCakeCustom}
              charges={cakeCharges}
              setCharges={setCakeCharges}
              totals={serviceTotals.Cake}
              expenses={expenses.Cake}
              setExpenses={(list) => setServiceExpenses('Cake', list)}
            />
          )}

          {currentStep === 'Return Gifts' && (
            <GiftStep
              custom={giftCustom}
              setCustom={setGiftCustom}
              charges={giftCharges}
              setCharges={setGiftCharges}
              totals={serviceTotals['Return Gifts']}
              expenses={expenses['Return Gifts']}
              setExpenses={(list) => setServiceExpenses('Return Gifts', list)}
            />
          )}

          {currentStep === 'Review' && (
            <ReviewStep
              event={event}
              quoteNo={quoteNo}
              selectedServices={selectedServices}
              serviceTotals={serviceTotals}
              catLines={catLines}
              snackLines={snackLines}
              decorLines={decorLines}
              decorGender={isBirthday ? decorGender : ''}
              photoLines={photoLines}
              entLines={entLines}
              cakeLines={cakeLines}
              giftLines={giftLines}
              guests={cateringGuests || guests}
              snackGuests={guests}
              catGuestSplit={{ nonveg: nonVegOn ? nonVegGuests : 0, veg: vegOn ? vegGuests : 0, total: cateringGuests }}
              cateringPerPlate={cateringPerPlate}
              grandTotal={grandTotal}
              grandProfit={grandProfit}
              grandProfitPct={grandProfitPct}
              grandPreProfit={grandPreProfit}
              expenses={expenses}
              overallExpenses={expenses.overall}
              setOverallExpenses={(list) => setServiceExpenses('overall', list)}
              finalRound={finalRound}
              setFinalRound={setFinalRound}
              finalRoundOff={finalRoundOff}
              rawGrandTotal={rawGrandTotal}
              onEditService={editService}
              onEditDetails={() => goToStep(0)}
              pdfMode={pdfMode}
              setPdfMode={setPdfMode}
            />
          )}
        </main>

        {/* Share sheet */}
        {shareOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShareOpen(false)} />
            <div className="relative w-full sm:max-w-[420px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="font-extrabold text-[16px]">Share quote</div>
                <button type="button" onClick={() => setShareOpen(false)} className="w-9 h-9 rounded-xl text-slate-500 active:bg-slate-100"><i className="fas fa-xmark text-lg" /></button>
              </div>
              <div className="text-[12.5px] text-slate-500 mb-4">Anyone with the link can open this exact quote in the builder.</div>
              <div className="space-y-2.5">
                <button type="button" onClick={doWhatsApp} className="w-full h-13 rounded-2xl text-white font-bold flex items-center justify-center gap-2.5 active:opacity-90" style={{ height: '52px', background: '#25D366' }}>
                  <i className="fab fa-whatsapp text-lg" /> Share on WhatsApp
                </button>
                <button type="button" onClick={doCopyLink} className="w-full rounded-2xl border-2 border-slate-200 text-slate-700 font-bold flex items-center justify-center gap-2.5 active:bg-slate-50" style={{ height: '52px' }}>
                  <i className="fas fa-link" /> Copy link
                </button>
                {typeof navigator !== 'undefined' && navigator.share && (
                  <button type="button" onClick={doNativeShare} className="w-full rounded-2xl border-2 border-slate-200 text-slate-700 font-bold flex items-center justify-center gap-2.5 active:bg-slate-50" style={{ height: '52px' }}>
                    <i className="fas fa-share-nodes" /> More options…
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Share toast */}
        {shareToast && (
          <div className="fixed left-1/2 -translate-x-1/2 z-[60]" style={{ bottom: '96px' }}>
            <div className="bg-slate-900 text-white text-[13px] font-semibold px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2">
              <i className="fas fa-circle-check text-[#3fe0c5]" /> {shareToast}
            </div>
          </div>
        )}

        {/* Sticky footer nav */}
        <footer className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200">
          <div className="max-w-[560px] mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Grand Total</span>
              <span className="text-xl font-extrabold text-[#16a085]">{fmtINR(grandTotal)}</span>
            </div>
            <div className="flex gap-2.5">
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="h-13 px-5 rounded-2xl border-2 border-slate-200 text-slate-600 font-bold active:bg-slate-50 flex items-center gap-2"
                  style={{ height: '52px' }}
                >
                  <i className="fas fa-arrow-left text-[13px]" /> Back
                </button>
              )}
              {currentStep === 'Review' ? (
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex-1 rounded-2xl text-white font-bold active:opacity-90 flex items-center justify-center gap-2 disabled:opacity-70"
                  style={{ height: '52px', background: 'linear-gradient(135deg,#1abc9c,#0f766e)' }}
                >
                  {downloading ? (
                    <><i className="fas fa-circle-notch fa-spin" /> Generating…</>
                  ) : (
                    <><i className="fas fa-file-arrow-down" /> PDF ({pdfMode === 'client' ? 'Client' : 'Detailed'})</>
                  )}
                </button>
              ) : null}
              {currentStep === 'Review' && (
                <button
                  type="button"
                  onClick={handleDownloadImage}
                  disabled={downloading}
                  className="rounded-2xl border-2 border-[#1abc9c] text-[#0f766e] font-bold active:bg-teal-50 flex items-center justify-center gap-2 disabled:opacity-70 px-4"
                  style={{ height: '52px' }}
                  aria-label="Download image"
                >
                  <i className="fas fa-image" /> PNG
                </button>
              )}
              {currentStep !== 'Review' && (
                <button
                  type="button"
                  onClick={goNext}
                  className="flex-1 rounded-2xl text-white font-bold active:opacity-90 flex items-center justify-center gap-2"
                  style={{ height: '52px', background: 'linear-gradient(135deg,#1abc9c,#0f766e)' }}
                >
                  Next <i className="fas fa-arrow-right text-[13px]" />
                </button>
              )}
            </div>
          </div>
        </footer>

        {/* Saved quotes drawer */}
        {savedOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSavedOpen(false)} />
            <div className="relative w-full max-w-[420px] bg-slate-50 h-full shadow-2xl flex flex-col">
              <div className="px-4 py-3.5 bg-white border-b border-slate-200 flex items-center justify-between">
                <div className="font-extrabold text-[15px]">Saved Quotes <span className="text-slate-400 font-semibold">({savedQuotes.length})</span></div>
                <button type="button" onClick={() => setSavedOpen(false)} className="w-9 h-9 rounded-xl text-slate-500 active:bg-slate-100">
                  <i className="fas fa-xmark text-lg" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                {savedQuotes.length === 0 && (
                  <div className="text-center text-slate-400 text-[13px] pt-16">
                    <i className="fas fa-folder-open text-3xl mb-3 block opacity-40" />
                    No saved quotes yet.<br />They save automatically once you enter a customer name.
                  </div>
                )}
                {savedQuotes.map((q) => (
                  <div key={q.quoteNo} className={`rounded-2xl border bg-white p-3.5 ${q.quoteNo === quoteNo ? 'border-[#1abc9c] ring-1 ring-[#1abc9c]/30' : 'border-slate-200'}`}>
                    <button type="button" onClick={() => loadQuote(q.quoteNo)} className="w-full text-left">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-bold text-[14px] truncate flex items-center gap-1.5">
                            {q.quoteType === 'package' && <i className="fas fa-box-open text-[11px] text-[#1abc9c]" />}
                            {q.customerName || '—'}
                          </div>
                          <div className="text-[11.5px] text-slate-500 mt-0.5">
                            {q.eventType}
                            {q.date && ` · ${new Date(q.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-extrabold text-[#16a085]">{fmtINR(q.grandTotal)}</div>
                          {q.quoteNo === quoteNo && <div className="text-[10px] font-bold text-[#1abc9c] mt-0.5">CURRENT</div>}
                        </div>
                      </div>
                      {q.services?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {q.services.map((s) => (
                            <span key={s} className="text-[10px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">{s}</span>
                          ))}
                        </div>
                      )}
                    </button>
                    <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-slate-100">
                      <span className="text-[10.5px] text-slate-400">
                        {q.savedAt && new Date(q.savedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      <div className="flex items-center gap-1">
                        {confirmDeleteId === q.quoteNo ? (
                          <>
                            <span className="text-[11px] font-semibold text-slate-500 mr-0.5">Delete?</span>
                            <button type="button" onClick={() => { deleteQuote(q.quoteNo); setConfirmDeleteId(null) }} className="text-[12px] font-bold text-white bg-rose-500 px-2.5 py-1 rounded-lg active:bg-rose-600">
                              Yes
                            </button>
                            <button type="button" onClick={() => setConfirmDeleteId(null)} className="text-[12px] font-semibold text-slate-500 px-2.5 py-1 rounded-lg active:bg-slate-100">
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" onClick={() => loadQuote(q.quoteNo)} className="text-[12px] font-semibold text-[#0f766e] px-2.5 py-1 rounded-lg active:bg-teal-50">
                              <i className="fas fa-folder-open mr-1 text-[11px]" /> Open
                            </button>
                            <button type="button" onClick={() => cloneQuote(q.quoteNo)} className="text-[12px] font-semibold text-[#0f766e] px-2.5 py-1 rounded-lg active:bg-teal-50">
                              <i className="fas fa-clone mr-1 text-[11px]" /> Clone
                            </button>
                            <button type="button" onClick={() => setConfirmDeleteId(q.quoteNo)} className="text-[12px] font-semibold text-rose-500 px-2.5 py-1 rounded-lg active:bg-rose-50">
                              <i className="fas fa-trash-can text-[11px]" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-3 py-3 bg-white border-t border-slate-200">
                <div className="text-[11px] text-slate-400 text-center">
                  Saved in this browser (localStorage) on this device.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full-screen mask shown while exporting — hides the print node, which is
          briefly moved on-screen (0,0) so html-to-image can rasterise it. */}
      {downloading && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-white/95 backdrop-blur-sm">
          <i className="fas fa-circle-notch fa-spin text-3xl text-[#1abc9c]" />
          <div className="text-[13px] font-semibold text-slate-600">Generating your file…</div>
        </div>
      )}

      {/* Printable summary — rendered off-screen, captured to a 1-page PDF */}
      <div className="qv2-print-area" ref={printRef}>
        <PrintSummary
          event={event}
          quoteNo={quoteNo}
          selectedServices={selectedServices}
          serviceTotals={serviceTotals}
          catLines={catLines}
          snackLines={snackLines}
          decorLines={decorLines}
          decorGender={isBirthday ? decorGender : ''}
          photoLines={photoLines}
          entLines={entLines}
          cakeLines={cakeLines}
          giftLines={giftLines}
          grandTotal={grandTotal}
          grandProfit={grandProfit}
          grandProfitPct={grandProfitPct}
          grandPreProfit={grandPreProfit}
          expenses={expenses}
          overallExpensesTotal={overallExpensesTotal}
          guests={cateringGuests || guests}
          snackGuests={guests}
          catGuestSplit={{ nonveg: nonVegOn ? nonVegGuests : 0, veg: vegOn ? vegGuests : 0, total: cateringGuests }}
          cateringPerPlate={cateringPerPlate}
          mode={pdfMode}
        />
      </div>
    </div>
  )
}

// ─── Step: Details ───────────────────────────────────────────────────────────
const DetailsStep = ({ event, setEvent, services, setServices, errors, showErrors, isIndiaCode, phoneMin, phoneMax, contactDigits }) => (
  <div className="space-y-3.5">
    {showErrors && Object.values(errors).some(Boolean) && (
      <div className="px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px] flex items-start gap-2">
        <i className="fas fa-exclamation-circle mt-0.5" />
        <span>Please fill the required fields marked <span className="font-bold">*</span> and pick at least one service.</span>
      </div>
    )}

    <Field label="Quote Type">
      <div className="grid grid-cols-2 gap-2">
        {[
          { v: 'customer', label: 'Customer', icon: 'fa-user' },
          { v: 'package', label: 'Package', icon: 'fa-box-open' },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setEvent({ ...event, quoteType: o.v })}
            className={`h-11 rounded-xl border-2 text-[13px] font-bold flex items-center justify-center gap-2 transition ${
              event.quoteType === o.v ? 'bg-[#1abc9c] text-white border-[#1abc9c]' : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50'
            }`}
          >
            <i className={`fas ${o.icon}`} /> {o.label}
          </button>
        ))}
      </div>
    </Field>

    {event.quoteType === 'package' && (
      <Field label="Package Name" required error={showErrors && errors.packageName}>
        <input
          className={`qv2-input ${showErrors && errors.packageName ? 'qv2-input-error' : ''}`}
          value={event.packageName || ''}
          onChange={(e) => setEvent({ ...event, packageName: e.target.value })}
          placeholder="e.g. Gold Birthday Package"
        />
      </Field>
    )}

    {event.quoteType !== 'package' && (
    <>
    <Field label="Customer Name" required error={showErrors && errors.guestName}>
      <div className="flex gap-2">
        <select
          className="qv2-input"
          style={{ width: '92px', flexShrink: 0 }}
          value={event.salutation}
          onChange={(e) => setEvent({ ...event, salutation: e.target.value })}
          aria-label="Salutation"
        >
          {SALUTATIONS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <input
          className={`qv2-input ${showErrors && errors.guestName ? 'qv2-input-error' : ''}`}
          value={event.guestName}
          onChange={(e) => setEvent({ ...event, guestName: e.target.value })}
          placeholder="Karthik"
        />
      </div>
    </Field>

    <Field label="Mobile Number" required error={showErrors && errors.contact}>
      <div className="flex gap-2">
        <select
          className="qv2-input"
          style={{ width: '96px', flexShrink: 0 }}
          value={event.contactCode}
          onChange={(e) => setEvent({ ...event, contactCode: e.target.value })}
          aria-label="Country code"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <input
          className={`qv2-input ${showErrors && errors.contact ? 'qv2-input-error' : ''}`}
          inputMode="numeric"
          type="tel"
          maxLength={phoneMax}
          value={event.contact}
          onChange={(e) => setEvent({ ...event, contact: e.target.value.replace(/\D/g, '').slice(0, phoneMax) })}
          placeholder={isIndiaCode ? '9444848616' : 'phone number'}
        />
      </div>
      {contactDigits.length > 0 && (contactDigits.length < phoneMin || contactDigits.length > phoneMax) && (
        <div className="text-[11px] text-rose-600 mt-1">
          {isIndiaCode ? `Phone must be exactly 10 digits (${contactDigits.length} entered)` : `Phone must be ${phoneMin}–${phoneMax} digits`}
        </div>
      )}
    </Field>

    <Field label="Event Type" required error={showErrors && errors.eventType}>
      <select
        className={`qv2-input ${showErrors && errors.eventType ? 'qv2-input-error' : ''}`}
        value={event.eventType}
        onChange={(e) => setEvent({ ...event, eventType: e.target.value })}
      >
        {EVENT_TYPES.map((t) => (
          <option key={t}>{t}</option>
        ))}
      </select>
    </Field>

    <Field label="Date" hint={event.date ? new Date(event.date).toLocaleDateString('en-IN', { weekday: 'long' }) : undefined}>
      <input type="date" className="qv2-input" value={event.date} onChange={(e) => setEvent({ ...event, date: e.target.value })} />
    </Field>

    <div className="grid grid-cols-2 gap-3">
      <Field label="Start Time">
        <input type="time" className="qv2-input" value={event.startTime || ''} onChange={(e) => setEvent({ ...event, startTime: e.target.value })} />
      </Field>
      <Field label="End Time">
        <input type="time" className="qv2-input" value={event.endTime || ''} onChange={(e) => setEvent({ ...event, endTime: e.target.value })} />
      </Field>
    </div>

    <Field label="Location" required error={showErrors && errors.location}>
      <input
        className={`qv2-input ${showErrors && errors.location ? 'qv2-input-error' : ''}`}
        value={event.location}
        onChange={(e) => setEvent({ ...event, location: e.target.value })}
        placeholder="Venue / Address"
      />
    </Field>
    </>
    )}

    <div>
      <div className="text-[11px] font-semibold mb-1.5 uppercase tracking-wide">
        <span className={showErrors && errors.services ? 'text-rose-600' : 'text-slate-500'}>Choose Services</span>
        <span className="text-rose-500 ml-0.5">*</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {SERVICES.map((s) => {
          const on = services[s.key]
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setServices({ ...services, [s.key]: !on })}
              className={`h-16 rounded-2xl border-2 transition flex items-center gap-3 px-4 ${
                on ? 'bg-[#1abc9c] text-white border-[#1abc9c]' : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50'
              }`}
            >
              <i className={`fas ${s.icon} text-lg`} />
              <span className="font-bold text-[13.5px] text-left leading-tight">{s.key}</span>
              {on && <i className="fas fa-check-circle ml-auto text-[13px]" />}
            </button>
          )
        })}
      </div>
      {showErrors && errors.services && <div className="text-[11px] text-rose-600 mt-1 font-medium">Pick at least one service</div>}
    </div>
  </div>
)

// ─── Step: Catering — Non-Veg + Veg (food) + Common (inclusive/service) ──────
// variant: 'nonveg' | 'veg' | 'common'. Food sections toggle + own guests;
// the common section is always shown and priced at the total catering guests.
const MenuSection = ({ label, variant, baseItems, s, commonGuests }) => {
  const isCommon = variant === 'common'
  const nonveg = variant === 'nonveg'
  const allItems = [...baseItems, ...s.custom]
  const selCount = allItems.filter((i) => s.selected[i.id] !== undefined).length
  const accent = isCommon ? 'border-slate-300' : nonveg ? 'border-rose-300' : 'border-emerald-300'
  const headBg = isCommon ? 'bg-slate-50' : nonveg ? 'bg-rose-50' : 'bg-emerald-50'
  const open = isCommon || s.on
  const cardGuests = isCommon ? commonGuests || 100 : s.guests
  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${open ? accent : 'border-slate-200'}`}>
      <button
        type="button"
        onClick={() => !isCommon && s.setOn(!s.on)}
        className={`w-full flex items-center gap-2.5 px-4 py-3 ${open ? headBg : 'bg-white'} ${isCommon ? 'cursor-default' : ''}`}
      >
        {isCommon ? (
          <i className="fas fa-layer-group text-slate-400 w-6 text-center" />
        ) : (
          <span className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center ${s.on ? (nonveg ? 'bg-rose-600 border-rose-600' : 'bg-emerald-600 border-emerald-600') : 'border-slate-300'}`}>
            {s.on && <i className="fas fa-check text-white text-[11px]" />}
          </span>
        )}
        {!isCommon && <VegDot nonveg={nonveg} />}
        <span className="font-extrabold text-[15px] text-slate-800">{label}</span>
        {isCommon && <span className="text-[11px] text-slate-400 font-normal">· all guests</span>}
        {open && (
          <span className="ml-auto text-[12px] font-semibold text-slate-500">
            {!isCommon && `${s.guests} guests · `}
            {selCount} item{selCount === 1 ? '' : 's'}
          </span>
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100 bg-white">
          {!isCommon && (
            <Field label={`${label} Guests`}>
              <GuestStepper value={s.guests} onChange={s.setGuests} />
            </Field>
          )}
          <div className="space-y-2.5">
            {allItems.map((item) => (
              <CatItemCard
                key={item.id}
                item={item}
                guests={cardGuests}
                packageName="Starter"
                selected={s.selected[item.id] !== undefined}
                override={s.selected[item.id] || {}}
                editing={!!s.editing[item.id]}
                onToggle={() => s.h.toggle(item)}
                onEditToggle={() => s.setEditing((e) => ({ ...e, [item.id]: !e[item.id] }))}
                setOverride={(k, v) => s.h.setOverride(item.id, k, v)}
                updateCustom={(k, v) => s.h.updateCustom(item.id, k, v)}
                removeCustom={() => s.h.removeCustom(item.id)}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={s.h.addCustom}
            className="w-full h-11 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 font-semibold text-[13px] active:bg-slate-50 flex items-center justify-center gap-2"
          >
            <i className="fas fa-plus" /> Add custom {isCommon ? 'item' : 'dish'}
          </button>
        </div>
      )}
    </div>
  )
}

const CateringStep = ({ nv, vg, common, cateringGuests, perPlate, charges, setCharges, totals, showErrors, expenses, setExpenses }) => (
  <div className="space-y-4">
    {showErrors && !nv.on && !vg.on && (
      <div className="px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px]">Enable at least one section (Non-Veg or Veg).</div>
    )}
    <MenuSection label="Non-Veg" variant="nonveg" baseItems={NONVEG_CATALOG} s={nv} />
    <MenuSection label="Veg" variant="veg" baseItems={VEG_CATALOG} s={vg} />
    <MenuSection label="Common" variant="common" baseItems={CAT_COMMON_ITEMS} s={common} commonGuests={cateringGuests} />
    <ExpenseEditor items={expenses} setItems={setExpenses} withQty />
    <ChargesEditor charges={charges} onChange={setCharges} />
    <ServiceTotalCard totals={totals} />
    {perPlate > 0 && cateringGuests > 0 && (
      <div className="flex items-center justify-between rounded-2xl p-4 text-white" style={{ background: 'linear-gradient(135deg,#1abc9c,#0f766e)' }}>
        <span className="font-bold text-[15px]">
          <i className="fas fa-utensils mr-2" />Per plate <span className="text-white/60 font-normal text-[13px]">({cateringGuests} guests)</span>
        </span>
        <span className="text-2xl font-extrabold">{fmtINR(perPlate)}</span>
      </div>
    )}
  </div>
)

// ─── Step: Snacks & Stalls (single guest count + category chips) ─────────────
const MenuStep = ({ baseItems, cats: catList, addLabel, guests, setGuests, perPlate, selected, filter, setFilter, editing, setEditing, onToggle, setOverride, custom, addCustom, updateCustom, removeCustom, charges, setCharges, totals, showErrors, expenses, setExpenses, expenseWithQty }) => {
  const allItems = [...baseItems, ...custom]
  const cats = ['All', ...catList]
  const visible = allItems.filter((i) => filter === 'All' || i.category === filter || (filter === 'Custom' && i.custom))
  const renderCard = (item) => (
    <CatItemCard
      key={item.id}
      item={item}
      guests={guests}
      packageName="Starter"
      selected={selected[item.id] !== undefined}
      override={selected[item.id] || {}}
      editing={!!editing[item.id]}
      onToggle={() => onToggle(item)}
      onEditToggle={() => setEditing((e) => ({ ...e, [item.id]: !e[item.id] }))}
      setOverride={(k, v) => setOverride(item.id, k, v)}
      updateCustom={(k, v) => updateCustom(item.id, k, v)}
      removeCustom={() => removeCustom(item.id)}
    />
  )
  return (
    <div className="space-y-4">
      <Field label="No. of Guests" error={showErrors && !(guests > 0)}>
        <GuestStepper value={guests} onChange={setGuests} />
        {showErrors && !(guests > 0) && <div className="text-[11px] text-rose-600 mt-1 font-medium">Enter guest count</div>}
      </Field>

      {/* category filter */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4">
        {cats.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilter(c)}
            className={`shrink-0 h-9 px-3.5 rounded-full text-[12px] font-semibold border transition ${
              filter === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="space-y-2.5">{visible.map(renderCard)}</div>

      <button
        type="button"
        onClick={addCustom}
        className="w-full h-12 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 font-semibold text-[13px] active:bg-slate-50 flex items-center justify-center gap-2"
      >
        <i className="fas fa-plus" /> {addLabel}
      </button>

      <ExpenseEditor items={expenses} setItems={setExpenses} withQty={expenseWithQty} />
      <ChargesEditor charges={charges} onChange={setCharges} />
      <ServiceTotalCard totals={totals} />
      {perPlate > 0 && guests > 0 && (
        <div className="flex items-center justify-between rounded-2xl p-4 text-white" style={{ background: 'linear-gradient(135deg,#1abc9c,#0f766e)' }}>
          <span className="font-bold text-[15px]">
            <i className="fas fa-utensils mr-2" />Per plate <span className="text-white/60 font-normal text-[13px]">({guests} guests)</span>
          </span>
          <span className="text-2xl font-extrabold">{fmtINR(perPlate)}</span>
        </div>
      )}
    </div>
  )
}

const CatItemCard = ({ item, guests, packageName, selected, override, editing, onToggle, onEditToggle, setOverride, updateCustom, removeCustom }) => {
  const calc = calcLine(item, guests, override, { packageName })
  const isInclusive = item.rule === 'inclusive'
  const descVal = override.description !== undefined ? override.description : item.description || ''
  if (!selected) {
    return (
      <button type="button" onClick={onToggle} className="w-full text-left bg-white rounded-2xl border border-slate-200 p-3.5 active:bg-slate-50 transition">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] leading-tight">{item.name || 'Custom dish'}</div>
            {item.description && <div className="text-[11.5px] text-slate-600 italic mt-0.5" style={{ whiteSpace: 'pre-line' }}>{item.description}</div>}
            <div className="text-[11px] text-slate-500 mt-0.5">
              {isInclusive ? (
                <span className="text-emerald-600 font-semibold">Inclusive</span>
              ) : item.rule === 'flat' ? (
                <>Flat · {fmtINR(item.flatPrice)}</>
              ) : item.rule === 'qty_offset' ? (
                <>Zero-line · qty = guests + {item.offset}</>
              ) : item.rule === 'package_qty' ? (
                <>{item.unitLabel} · ₹{item.rate} × {calc.qty}</>
              ) : (
                <>{item.unitLabel} · ₹{item.rate?.toLocaleString('en-IN')}{item.serves > 0 && ` · serves ${item.serves}/${item.unitLabel?.toLowerCase()}`}</>
              )}
            </div>
            {item.note && <div className="text-[10.5px] text-amber-700 mt-0.5">{item.note}</div>}
          </div>
          <div className="text-right shrink-0">
            {!isInclusive && <div className="font-bold text-slate-700">{fmtINR(calc.total)}</div>}
            <div className="w-9 h-9 mt-1 ml-auto rounded-xl bg-[#1abc9c] text-white flex items-center justify-center"><i className="fas fa-plus text-[13px]" /></div>
          </div>
        </div>
      </button>
    )
  }
  return (
    <div className="bg-white rounded-2xl border-2 border-[#1abc9c] p-3.5 space-y-2.5">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <i className="fas fa-circle-check text-[#1abc9c] text-[13px]" />
            {item.custom ? (
              <input className="qv2-input" style={{ padding: '0.4rem 0.6rem', fontSize: '15px' }} placeholder="Dish name" value={item.name} onChange={(e) => updateCustom('name', e.target.value)} />
            ) : (
              <span className="text-[15px]" style={{ fontWeight: override.bold ? 800 : 600 }}>{item.name}</span>
            )}
            {override.label && (
              <span className="text-[10px] font-bold text-white bg-rose-600 rounded-full px-2 py-0.5 uppercase tracking-wide">{override.label}</span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{item.category}</div>
          {descVal && <div className="text-[11.5px] text-slate-600 italic mt-0.5" style={{ whiteSpace: 'pre-line' }}>{descVal}</div>}
        </div>
        <button type="button" onClick={item.custom ? removeCustom : onToggle} className="text-rose-400 w-8 h-8 rounded-lg active:bg-rose-50" aria-label="Remove">
          <i className="fas fa-xmark" />
        </button>
      </div>

      {!isInclusive && (
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-slate-500">
            {calc.qty} {item.unitLabel} × {fmtINR(calc.unitPrice)}
            {calc.surchargeNote && <span className="text-amber-700"> · {calc.surchargeNote}</span>}
          </span>
          <span className="font-extrabold text-[#16a085] text-base">{fmtINR(calc.total)}</span>
        </div>
      )}

      <button type="button" onClick={onEditToggle} className="text-[12px] font-semibold text-slate-500 flex items-center gap-1.5">
        <i className="fas fa-sliders text-[11px]" /> Customize
      </button>

      {editing && (
        <div className="pt-1 border-t border-slate-100 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {item.custom ? (
              <>
                <Field label="Qty">
                  <input type="number" inputMode="numeric" className="qv2-input" placeholder="1" value={override.qty ?? ''} onChange={(e) => setOverride('qty', e.target.value)} />
                </Field>
                <Field label="Unit Price">
                  <MoneyInput value={item.flatPrice} onChange={(v) => updateCustom('flatPrice', v)} />
                </Field>
              </>
            ) : (
              <>
                <Field label="Qty override">
                  <input type="number" inputMode="numeric" className="qv2-input" placeholder={String(calc.qty)} value={override.qty ?? ''} onChange={(e) => setOverride('qty', e.target.value)} />
                </Field>
                <Field label="Unit Price override">
                  <input type="number" inputMode="numeric" className="qv2-input" placeholder={String(calc.unitPrice)} value={override.unitPrice ?? ''} onChange={(e) => setOverride('unitPrice', e.target.value)} />
                </Field>
              </>
            )}
          </div>
          <Field label="Description">
            <input
              className="qv2-input"
              placeholder="e.g. Pineapple & Watermelon, Butterscotch…"
              value={descVal}
              onChange={(e) => setOverride('description', e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <div className="text-[11px] font-semibold mb-1 uppercase tracking-wide text-slate-500">Label (red badge)</div>
              <input
                className="qv2-input"
                placeholder="e.g. Signature Dish"
                value={override.label ?? ''}
                onChange={(e) => setOverride('label', e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => setOverride('bold', !override.bold)}
              className={`h-11 rounded-xl font-bold text-[13px] border-2 transition flex items-center justify-center gap-2 ${
                override.bold ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              <span style={{ fontWeight: 800 }}>B</span> Bold name
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['Signature Dish', "Chef's Special", 'Bestseller', 'Recommended'].map((s) => (
              <button key={s} type="button" onClick={() => setOverride('label', override.label === s ? '' : s)} className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${override.label === s ? 'bg-rose-600 text-white border-rose-600' : 'text-rose-600 bg-rose-50 border-rose-200'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Step: Decoration ────────────────────────────────────────────────────────
const DecorationStep = ({ isBirthday, gender, setGender, base, setBase, prices, setPrices, inclusions, setInclusions, custom, setCustom, charges, setCharges, totals, expenses, setExpenses }) => {
  const editInclusion = (key, idx, value) =>
    setInclusions({ ...inclusions, [key]: inclusions[key].map((it, i) => (i === idx ? { ...normItem(it), text: value } : normItem(it))) })
  const toggleBoldInclusion = (key, idx) =>
    setInclusions({ ...inclusions, [key]: inclusions[key].map((it, i) => (i === idx ? { ...normItem(it), bold: !normItem(it).bold } : normItem(it))) })
  const removeInclusion = (key, idx) =>
    setInclusions({ ...inclusions, [key]: inclusions[key].filter((_, i) => i !== idx) })
  const addInclusion = (key) => setInclusions({ ...inclusions, [key]: [...inclusions[key].map(normItem), { text: '', bold: false }] })
  return (
  <div className="space-y-4">
    {isBirthday && (
      <Field label="Baby Gender" hint="For birthday theming — recorded on the quote.">
        <div className="grid grid-cols-2 gap-2">
          {['Boy', 'Girl'].map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGender(gender === g ? '' : g)}
              className={`h-12 rounded-xl font-bold border-2 transition flex items-center justify-center gap-2 ${
                gender === g ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              <i className={`fas ${g === 'Boy' ? 'fa-child' : 'fa-child-dress'}`} /> {g}
            </button>
          ))}
        </div>
      </Field>
    )}

    <div>
      <div className="text-[11px] font-semibold mb-1.5 uppercase tracking-wide text-slate-500">Decoration Package</div>
      <div className="space-y-2.5">
        {[
          { key: 'without', label: 'Decoration', priceKey: 'without' },
          { key: 'with', label: 'Decoration with Entry', priceKey: 'with' },
        ].map((opt) => {
          const on = base === opt.key
          const list = inclusions[opt.key] || []
          return (
            <div key={opt.key} className={`rounded-2xl border-2 p-3.5 transition ${on ? 'border-[#1abc9c] bg-[#1abc9c]/5' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setBase(on ? null : opt.key)} className="flex-1 flex items-center gap-3 text-left">
                  <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${on ? 'border-[#1abc9c] bg-[#1abc9c]' : 'border-slate-300'}`}>
                    {on && <i className="fas fa-check text-white text-[10px]" />}
                  </span>
                  <span className="font-bold text-[15px]">{opt.label}</span>
                </button>
                <div className="w-32">
                  <MoneyInput value={prices[opt.priceKey]} onChange={(v) => setPrices({ ...prices, [opt.priceKey]: v })} />
                </div>
              </div>

              {on && (
                <div className="mt-3 pt-3 border-t border-[#1abc9c]/20">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                    Included ({list.length}) · tap to edit
                  </div>
                  <div className="space-y-1.5">
                    {list.map((raw, idx) => {
                      const it = normItem(raw)
                      return (
                      <div key={idx} className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => toggleBoldInclusion(opt.key, idx)}
                          className={`w-7 h-8 shrink-0 rounded-lg text-[11px] font-bold ${it.bold ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}
                          title="Toggle bold"
                        >
                          B
                        </button>
                        <input
                          className="qv2-input"
                          style={{ padding: '0.4rem 0.6rem', fontSize: '13px', fontWeight: it.bold ? 700 : 400 }}
                          value={it.text}
                          placeholder="Inclusion"
                          onChange={(e) => editInclusion(opt.key, idx, e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => removeInclusion(opt.key, idx)}
                          className="w-8 h-8 shrink-0 rounded-lg text-rose-400 active:bg-rose-50"
                          aria-label="Remove inclusion"
                        >
                          <i className="fas fa-xmark text-[13px]" />
                        </button>
                      </div>
                      )
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => addInclusion(opt.key)}
                    className="mt-2 text-[12px] font-semibold text-[#0f766e] flex items-center gap-1.5"
                  >
                    <i className="fas fa-plus text-[10px]" /> Add inclusion
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>

    <div>
      <div className="text-[11px] font-semibold mb-1.5 uppercase tracking-wide text-slate-500">Extra Decoration Items</div>
      <CustomItems items={custom} setItems={setCustom} addLabel="Add decoration item" namePlaceholder="e.g. Balloon arch, Flower wall" />
    </div>

    <ExpenseEditor items={expenses} setItems={setExpenses} />
    <ChargesEditor charges={charges} onChange={setCharges} />
    <ServiceTotalCard totals={totals} />
  </div>
  )
}

// ─── Step: Photography ───────────────────────────────────────────────────────
const PhotographyStep = ({ packages, selected, onToggle, updatePkg, custom, setCustom, charges, setCharges, totals, expenses, setExpenses }) => {
  // Normalise items on every write so legacy string-based saved quotes upgrade cleanly.
  const editLine = (pkg, idx, value) => updatePkg(pkg.id, { items: pkg.items.map((it, i) => (i === idx ? { ...normItem(it), text: value } : normItem(it))) })
  const toggleSection = (pkg, idx) => updatePkg(pkg.id, { items: pkg.items.map((it, i) => (i === idx ? { ...normItem(it), section: !normItem(it).section } : normItem(it))) })
  const toggleBold = (pkg, idx) => updatePkg(pkg.id, { items: pkg.items.map((it, i) => (i === idx ? { ...normItem(it), bold: !normItem(it).bold } : normItem(it))) })
  const removeLine = (pkg, idx) => updatePkg(pkg.id, { items: pkg.items.filter((_, i) => i !== idx) })
  // Insert a new item at a specific position (so items land in the intended section).
  const addItemAt = (pkg, insertAt) => {
    const items = pkg.items.map(normItem)
    updatePkg(pkg.id, { items: [...items.slice(0, insertAt), { text: '', section: false, bold: false }, ...items.slice(insertAt)] })
  }
  const addSection = (pkg) => updatePkg(pkg.id, { items: [...pkg.items.map(normItem), { text: '', section: true, bold: false }] })
  // Group items by their preceding section so each group can add its own items.
  const buildGroups = (items) => {
    const groups = [{ headerIdx: -1, section: null, items: [] }]
    items.forEach((raw, idx) => {
      const it = normItem(raw)
      if (it.section) groups.push({ headerIdx: idx, section: it.text, items: [] })
      else groups[groups.length - 1].items.push({ idx, text: it.text, bold: it.bold })
    })
    return groups.filter((g, i) => i === 0 || g.section !== null || g.items.length) // keep main + real sections
  }
  return (
    <div className="space-y-4">
      <div className="space-y-2.5">
        {packages.map((pkg) => {
          const on = !!selected[pkg.id]
          return (
            <div key={pkg.id} className={`rounded-2xl border-2 p-3.5 transition ${on ? 'border-[#1abc9c] bg-[#1abc9c]/5' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => onToggle(pkg.id)}
                  className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center ${on ? 'border-[#1abc9c] bg-[#1abc9c]' : 'border-slate-300'}`}
                  aria-label={`Select ${pkg.name}`}
                >
                  {on && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
                </button>
                <input
                  className="qv2-input font-bold"
                  style={{ padding: '0.45rem 0.65rem', fontSize: '15px' }}
                  value={pkg.name}
                  placeholder="Package name"
                  onChange={(e) => updatePkg(pkg.id, { name: e.target.value })}
                />
                <div className="w-28 shrink-0">
                  <MoneyInput value={pkg.price} onChange={(v) => updatePkg(pkg.id, { price: v })} />
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-slate-200/70">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  Included ({pkg.items.length}) · tap to edit
                </div>
                <div className="space-y-2.5">
                  {buildGroups(pkg.items).map((grp, gi) => {
                    const insertAt = grp.items.length ? grp.items[grp.items.length - 1].idx + 1 : grp.headerIdx >= 0 ? grp.headerIdx + 1 : 0
                    return (
                      <div key={gi} className="space-y-1.5">
                        {grp.section !== null && (
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => toggleSection(pkg, grp.headerIdx)}
                              className="w-6 h-8 shrink-0 rounded-lg flex items-center justify-center text-[11px] text-slate-700 bg-slate-100"
                              title="Section heading — tap to make it an item"
                            >
                              <i className="fas fa-heading" />
                            </button>
                            <input
                              className="qv2-input"
                              style={{ padding: '0.4rem 0.6rem', fontSize: '13px', fontWeight: 700, textTransform: 'uppercase' }}
                              value={grp.section}
                              placeholder="Section heading"
                              onChange={(e) => editLine(pkg, grp.headerIdx, e.target.value)}
                            />
                            <button type="button" onClick={() => removeLine(pkg, grp.headerIdx)} className="w-8 h-8 shrink-0 rounded-lg text-rose-400 active:bg-rose-50" aria-label="Remove section">
                              <i className="fas fa-xmark text-[13px]" />
                            </button>
                          </div>
                        )}
                        {grp.items.map((it) => (
                          <div key={it.idx} className="flex items-center gap-1.5" style={{ paddingLeft: grp.section !== null ? 10 : 0 }}>
                            <button
                              type="button"
                              onClick={() => toggleSection(pkg, it.idx)}
                              className="w-6 h-8 shrink-0 rounded-lg flex items-center justify-center text-[11px] text-[#1abc9c]"
                              title="Item — tap to make it a section heading"
                            >
                              <i className="fas fa-check" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleBold(pkg, it.idx)}
                              className={`w-6 h-8 shrink-0 rounded-lg text-[11px] font-bold ${it.bold ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}
                              title="Toggle bold"
                            >
                              B
                            </button>
                            <input
                              className="qv2-input"
                              style={{ padding: '0.4rem 0.6rem', fontSize: '13px', fontWeight: it.bold ? 700 : 400 }}
                              value={it.text}
                              placeholder="Inclusion"
                              onChange={(e) => editLine(pkg, it.idx, e.target.value)}
                            />
                            <button type="button" onClick={() => removeLine(pkg, it.idx)} className="w-8 h-8 shrink-0 rounded-lg text-rose-400 active:bg-rose-50" aria-label="Remove line">
                              <i className="fas fa-xmark text-[13px]" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addItemAt(pkg, insertAt)}
                          className="text-[11.5px] font-semibold text-[#0f766e] flex items-center gap-1.5"
                          style={{ paddingLeft: grp.section !== null ? 10 : 0 }}
                        >
                          <i className="fas fa-plus text-[10px]" /> Add item{grp.section ? ' here' : ''}
                        </button>
                      </div>
                    )
                  })}
                </div>
                <div className="mt-3 pt-2 border-t border-slate-100">
                  <button type="button" onClick={() => addSection(pkg)} className="text-[12px] font-semibold text-slate-500 flex items-center gap-1.5">
                    <i className="fas fa-heading text-[10px]" /> Add section
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div>
        <div className="text-[11px] font-semibold mb-1.5 uppercase tracking-wide text-slate-500">Extra Photography Items</div>
        <CustomItems items={custom} setItems={setCustom} addLabel="Add photography item" namePlaceholder="e.g. Drone shots, LED wall" />
      </div>

      <ExpenseEditor items={expenses} setItems={setExpenses} />
      <ChargesEditor charges={charges} onChange={setCharges} />
      <ServiceTotalCard totals={totals} />
    </div>
  )
}

// ─── Step: Entertainment ─────────────────────────────────────────────────────
const EntertainmentStep = ({ options, onToggle, update, addCustom, remove, charges, setCharges, totals, expenses, setExpenses }) => (
  <div className="space-y-4">
    <div className="text-[13px] text-slate-500 bg-slate-100 rounded-xl px-3.5 py-2.5">
      Select entertainment options. Each has editable name, quantity, price &amp; description.
    </div>
    <div className="space-y-2.5">
      {options.map((o) => {
        const on = !!o.on
        const lineTotal = Math.round(toNum(o.qty) * toNum(o.price))
        return (
          <div key={o.id} className={`rounded-2xl border-2 p-3.5 transition ${on ? 'border-[#1abc9c] bg-[#1abc9c]/5' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => onToggle(o.id)}
                className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center ${on ? 'border-[#1abc9c] bg-[#1abc9c]' : 'border-slate-300'}`}
                aria-label={`Select ${o.name}`}
              >
                {on && <i className="fas fa-check text-white text-[11px]" />}
              </button>
              {o.custom ? (
                <input className="qv2-input font-bold" style={{ padding: '0.45rem 0.65rem', fontSize: '15px' }} placeholder="Name" value={o.name} onChange={(e) => update(o.id, { name: e.target.value })} />
              ) : (
                <span className="flex-1 font-bold text-[15px]" style={{ fontWeight: o.bold ? 800 : 700 }}>{o.name}</span>
              )}
              <div className="w-28 shrink-0"><MoneyInput value={o.price} onChange={(v) => update(o.id, { price: v })} /></div>
              {o.custom && (
                <button type="button" onClick={() => remove(o.id)} className="w-9 h-9 shrink-0 rounded-xl text-rose-500 bg-rose-50 active:bg-rose-100" aria-label="Remove"><i className="fas fa-trash-can text-[13px]" /></button>
              )}
            </div>
            {on && (
              <div className="mt-3 pt-3 border-t border-[#1abc9c]/20 space-y-2">
                <div className="flex items-end gap-2">
                  <div className="w-20">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Qty</div>
                    <input type="number" inputMode="numeric" min="1" className="qv2-input text-center" value={showNum(o.qty)} onChange={(e) => update(o.id, { qty: toNum(e.target.value) })} />
                  </div>
                  <button
                    type="button"
                    onClick={() => update(o.id, { bold: !o.bold })}
                    className={`h-11 px-3 rounded-xl font-bold text-[13px] border-2 flex items-center gap-2 ${o.bold ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
                  >
                    <span style={{ fontWeight: 800 }}>B</span> Bold
                  </button>
                  <div className="flex-1 text-right pb-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Line Total</div>
                    <div className="font-extrabold text-[#16a085]">{fmtINR(lineTotal)}</div>
                  </div>
                </div>
                <input className="qv2-input" style={{ fontSize: '13px' }} placeholder="Description (optional)" value={o.desc || ''} onChange={(e) => update(o.id, { desc: e.target.value })} />
              </div>
            )}
          </div>
        )
      })}
    </div>
    <button type="button" onClick={addCustom} className="w-full h-12 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 font-semibold text-[13px] active:bg-slate-50 flex items-center justify-center gap-2">
      <i className="fas fa-plus" /> Add custom entertainment
    </button>
    <ExpenseEditor items={expenses} setItems={setExpenses} />
    <ChargesEditor charges={charges} onChange={setCharges} />
    <ServiceTotalCard totals={totals} />
  </div>
)

// ─── Step: Cake ──────────────────────────────────────────────────────────────
const CakeStep = ({ on, setOn, price, setPrice, desc, setDesc, custom, setCustom, charges, setCharges, totals, expenses, setExpenses }) => (
  <div className="space-y-4">
    <div className={`rounded-2xl border-2 p-3.5 transition ${on ? 'border-[#1abc9c] bg-[#1abc9c]/5' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setOn(!on)} className="flex-1 flex items-center gap-3 text-left">
          <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${on ? 'border-[#1abc9c] bg-[#1abc9c]' : 'border-slate-300'}`}>
            {on && <i className="fas fa-check text-white text-[10px]" />}
          </span>
          <span className="font-bold text-[15px]"><i className="fas fa-cake-candles text-[#1abc9c] mr-2" />Birthday Cake</span>
        </button>
        <div className="w-32">
          <MoneyInput value={price} onChange={setPrice} />
        </div>
      </div>
      {on && (
        <div className="mt-3 pt-3 border-t border-[#1abc9c]/20">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Description</div>
          <textarea
            className="qv2-input"
            rows={2}
            style={{ resize: 'vertical' }}
            placeholder="e.g. 2 kg Chocolate truffle, eggless, name printed…"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </div>
      )}
    </div>

    <div>
      <div className="text-[11px] font-semibold mb-1.5 uppercase tracking-wide text-slate-500">Extra Cake Items</div>
      <CustomItems items={custom} setItems={setCustom} addLabel="Add cake item" namePlaceholder="e.g. Cupcakes, Dessert table" withDescription />
    </div>

    <ExpenseEditor items={expenses} setItems={setExpenses} />
    <ChargesEditor charges={charges} onChange={setCharges} />
    <ServiceTotalCard totals={totals} />
  </div>
)

// ─── Step: Return Gifts ──────────────────────────────────────────────────────
const GiftStep = ({ custom, setCustom, charges, setCharges, totals, expenses, setExpenses }) => (
  <div className="space-y-4">
    <div className="text-[13px] text-slate-500 bg-slate-100 rounded-xl px-3.5 py-2.5">
      Add return gift items with quantity, price and a description. Everything here is fully customizable.
    </div>
    <CustomItems items={custom} setItems={setCustom} addLabel="Add return gift" namePlaceholder="e.g. Sweet box, Keychain" withDescription />
    <ExpenseEditor items={expenses} setItems={setExpenses} />
    <ChargesEditor charges={charges} onChange={setCharges} />
    <ServiceTotalCard totals={totals} />
  </div>
)

// ─── shared: per-service total card ───────────────────────────────────────────
const ServiceTotalCard = ({ totals }) => (
  <div className="rounded-2xl bg-slate-900 text-white p-4 space-y-1.5">
    <Row label="Items subtotal" value={fmtINR(totals.subtotal)} muted />
    {totals.expenses > 0 && <Row label="Other expenses" value={fmtINR(totals.expenses)} muted />}
    <div className="flex items-center justify-between text-[13px] pt-1.5 mt-1.5 border-t border-white/10">
      <span className="text-white/80 font-semibold">Subtotal (cost)</span>
      <span className="font-semibold">{fmtINR(totals.cost)}</span>
    </div>
    <Row label={`Profit (${totals.totalProfitPct}%)`} value={fmtINR(totals.totalProfit)} muted />
    <div className="border-t border-white/15 my-1.5" />
    <div className="flex items-center justify-between">
      <span className="font-bold">Service Total</span>
      <span className="text-xl font-extrabold text-[#3fe0c5]">{fmtINR(totals.total)}</span>
    </div>
  </div>
)
const Row = ({ label, value, muted }) => (
  <div className="flex items-center justify-between text-[13px]">
    <span className={muted ? 'text-white/60' : ''}>{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
)

// Veg / Non-Veg square marker (green = veg, red = non-veg).
const VegDot = ({ nonveg }) => (
  <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-[3px] border ${nonveg ? 'border-rose-600' : 'border-emerald-600'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${nonveg ? 'bg-rose-600' : 'bg-emerald-600'}`} />
  </span>
)

// Small label-over-value stat used in the review section breakdown band.
const BreakStat = ({ label, value, tone = 'text-slate-700' }) => (
  <div className="leading-tight">
    <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-[13px] font-bold ${tone}`}>{value}</div>
  </div>
)

// ─── Step: Review (on-screen) ─────────────────────────────────────────────────
const ReviewStep = ({ event, quoteNo, selectedServices, serviceTotals, catLines, snackLines, decorLines, decorGender, photoLines, entLines, cakeLines, giftLines, guests, snackGuests, catGuestSplit, cateringPerPlate, grandTotal, grandProfit, grandProfitPct, grandPreProfit, expenses, overallExpenses, setOverallExpenses, finalRound, setFinalRound, finalRoundOff, rawGrandTotal, onEditService, onEditDetails, pdfMode, setPdfMode }) => {
  const linesFor = { Catering: catLines, 'Snacks & Stalls': snackLines, Decoration: decorLines, Photography: photoLines, Entertainment: entLines, Cake: cakeLines, 'Return Gifts': giftLines }
  // Only show services that actually have items — skip selected-but-empty ones.
  const displayServices = selectedServices.filter((k) => (linesFor[k] || []).length > 0)
  const decorShown = displayServices.includes('Decoration')
  // The "Common" grouping only makes sense when both Non-Veg and Veg are present;
  // with a single section its items just fold in without a heading.
  const bothCatSections = catGuestSplit && catGuestSplit.nonveg > 0 && catGuestSplit.veg > 0
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-start justify-between">
          {event.quoteType === 'package' ? (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#1abc9c]"><i className="fas fa-box-open mr-1" />Package</div>
              <div className="text-[15px] font-extrabold mt-0.5">{event.packageName || '—'}</div>
            </div>
          ) : (
            <div className="text-[15px] font-extrabold">{[event.salutation, event.guestName].filter(Boolean).join(' ') || '—'}</div>
          )}
          <button type="button" onClick={onEditDetails} className="text-[12px] font-semibold text-[#0f766e] flex items-center gap-1 shrink-0 active:opacity-70">
            <i className="fas fa-pen text-[10px]" /> Edit
          </button>
        </div>
        {event.quoteType !== 'package' && (
          <>
            <div className="text-[12.5px] text-slate-500 mt-0.5">{event.contactCode} {event.contact}</div>
            <div className="text-[12.5px] text-slate-600 mt-2 flex flex-wrap gap-x-3 gap-y-1">
              <span><i className="fas fa-tag text-slate-400 mr-1" />{event.eventType}{decorGender && !decorShown && ` · ${decorGender}`}</span>
              {event.date && <span><i className="fas fa-calendar text-slate-400 mr-1" />{new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
              {(event.startTime || event.endTime) && <span><i className="fas fa-clock text-slate-400 mr-1" />{fmtTime12(event.startTime)} – {fmtTime12(event.endTime)}</span>}
            </div>
            {event.location && <div className="text-[12.5px] text-slate-600 mt-1"><i className="fas fa-location-dot text-slate-400 mr-1" />{event.location}</div>}
          </>
        )}
      </div>

      {displayServices.length === 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 text-center text-[13px] text-slate-400">
          No items added yet. Go back and add items to your selected services.
        </div>
      )}

      {displayServices.map((key) => {
        const t = serviceTotals[key]
        const lines = linesFor[key]
        // Per-plate widget (Catering + Snacks & Stalls) shown at the section foot.
        const ppGuests = key === 'Catering' ? catGuestSplit?.total || guests : key === 'Snacks & Stalls' ? snackGuests : 0
        const perPlate = ppGuests > 0 && (key === 'Catering' || key === 'Snacks & Stalls') ? Math.round(t.total / ppGuests) : 0
        // Profit markup factor — each item's price after the service profit % is applied.
        const markup = t.subtotal > 0 ? (t.subtotal + t.profit) / t.subtotal : 1
        return (
          <div key={key} className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[14px]">
                  {key}
                  {key === 'Decoration' && decorGender && (
                    <span className="font-normal text-slate-400 text-[12px]"> · Baby: {decorGender}</span>
                  )}
                </span>
                <button type="button" onClick={() => onEditService(key)} className="text-[12px] font-semibold text-[#0f766e] flex items-center gap-1 active:opacity-70 shrink-0">
                  <i className="fas fa-pen text-[10px]" /> Edit
                </button>
              </div>
            </div>
            <div className="px-4 py-2 divide-y divide-slate-100">
              {lines.map((l, i) => {
                const hasItems = l.items?.length > 0
                const grp = (x) => (x?.veg === false ? 'Non-Veg' : x?.veg === true ? 'Veg' : x?.veg == null && key === 'Catering' ? 'Common' : null)
                const gLabel = grp(l)
                const showHeader =
                  key === 'Catering' &&
                  gLabel &&
                  (i === 0 || grp(lines[i - 1]) !== gLabel) &&
                  !(gLabel === 'Common' && !bothCatSections)
                return (
                  <React.Fragment key={i}>
                  {showHeader && (
                    <div
                      className={`-mx-4 px-4 py-1.5 flex items-center gap-1.5 border-l-4 ${
                        gLabel === 'Non-Veg'
                          ? 'bg-rose-50 border-rose-500'
                          : gLabel === 'Veg'
                          ? 'bg-emerald-50 border-emerald-500'
                          : 'bg-slate-100 border-slate-400'
                      }`}
                    >
                      {gLabel !== 'Common' && <VegDot nonveg={gLabel === 'Non-Veg'} />}
                      <span className={`text-[11px] font-extrabold uppercase tracking-wide ${gLabel === 'Non-Veg' ? 'text-rose-700' : gLabel === 'Veg' ? 'text-emerald-700' : 'text-slate-600'}`}>{gLabel}</span>
                      {catGuestSplit && (
                        <span className={`text-[10.5px] font-bold ${gLabel === 'Non-Veg' ? 'text-rose-500' : gLabel === 'Veg' ? 'text-emerald-600' : 'text-slate-500'}`}>· {gLabel === 'Non-Veg' ? catGuestSplit.nonveg : gLabel === 'Veg' ? catGuestSplit.veg : catGuestSplit.total} guests</span>
                      )}
                    </div>
                  )}
                  <div className="py-2 text-[13px] flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {!hasItems && (
                        <div className="text-slate-600 flex items-center gap-1.5 flex-wrap">
                          <span style={{ fontWeight: l.bold ? 800 : 400 }}>{l.name || l.item?.name || 'Item'}</span>
                          {l.label && <span className="text-[9.5px] font-bold text-white bg-rose-600 rounded-full px-1.5 py-0.5 uppercase tracking-wide">{l.label}</span>}
                        </div>
                      )}
                      {!hasItems && (l.item ? (
                        <div className="text-[11px] text-slate-400 mt-0.5">{l.qty} {l.unitLabel} × {fmtINR(l.unitPrice)}</div>
                      ) : l.qty > 1 ? (
                        <div className="text-[11px] text-slate-400 mt-0.5">{l.qty} × {fmtINR(l.price)}</div>
                      ) : null)}
                      {l.description && <div className="text-[11.5px] text-slate-500 italic mt-0.5">{l.description}</div>}
                      {hasItems && (
                        <div className="text-[11.5px] text-slate-500 leading-relaxed">
                          {groupItems(l.items).map((g, gi) => (
                            <div key={gi} className={g.section ? 'mt-1' : ''}>
                              {g.section && <div className="font-semibold text-slate-600">{g.section}</div>}
                              {g.entries.filter((e) => e.text).map((e, ei) => (
                                <div key={ei} className="flex gap-1.5">
                                  <span className="text-[#1abc9c]">·</span>
                                  <span style={{ fontWeight: e.bold ? 700 : 400, color: e.bold ? '#0f172a' : undefined }}>{e.text}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {(() => {
                      const base = Number(l.total) || 0
                      const withProfit = Math.round(base * markup)
                      // Per-guest share of this item, by its section's head count
                      // (Catering: Non-Veg / Veg / Common counts; Snacks: snack guests).
                      const secGuests =
                        key === 'Catering'
                          ? l.veg === false
                            ? catGuestSplit?.nonveg || 0
                            : l.veg === true
                            ? catGuestSplit?.veg || 0
                            : catGuestSplit?.total || 0
                          : key === 'Snacks & Stalls'
                          ? snackGuests || 0
                          : 0
                      const perMember = secGuests > 0 && withProfit > 0 ? Math.round(withProfit / secGuests) : 0
                      return (
                        <div className="shrink-0 text-right leading-tight">
                          <div className="font-bold text-[#0f766e] tabular-nums">{fmtINR(withProfit)}</div>
                          {withProfit !== base && <div className="text-[10px] text-slate-400 tabular-nums">cost {fmtINR(base)}</div>}
                          {perMember > 0 && <div className="text-[10px] font-semibold text-[#1abc9c] tabular-nums">{fmtINR(perMember)} / guest</div>}
                        </div>
                      )
                    })()}
                  </div>
                  </React.Fragment>
                )
              })}
              {(expenses?.[key] || []).filter((e) => e.name || e.amount).map((e) => (
                <div key={e.id} className="py-1.5 flex items-center justify-between text-[12.5px] text-amber-700">
                  <span className="flex items-center gap-1.5"><i className="fas fa-receipt text-[11px]" />{e.name || 'Expense'}{toNum(e.qty) > 1 ? ` ×${toNum(e.qty)}` : ''}</span>
                  <span className="font-semibold">{fmtINR(toNum(e.qty ?? 1) * toNum(e.amount))}</span>
                </div>
              ))}
              <div className="mt-2 mx-3 mb-3 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5 text-[12.5px]">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Items</span>
                  <span className="font-semibold text-slate-700 tabular-nums">{fmtINR(t.subtotal)}</span>
                </div>
                {t.expenses > 0 && (
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-amber-700">Other expenses</span>
                    <span className="font-semibold text-amber-700 tabular-nums">+ {fmtINR(t.expenses)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-200">
                  <span className="text-slate-500">Subtotal <span className="text-slate-400">(cost)</span></span>
                  <span className="font-semibold text-slate-700 tabular-nums">{fmtINR(t.cost)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-slate-500">Profit <span className="text-slate-400">· {t.totalProfitPct}%</span></span>
                  <span className={`font-semibold tabular-nums ${t.totalProfit < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{t.totalProfit >= 0 ? '+ ' : ''}{fmtINR(t.totalProfit)}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-200">
                  <span className="font-bold text-slate-700 uppercase tracking-wide text-[11px]">Total</span>
                  <span className="text-[17px] font-extrabold text-[#0f766e] leading-none tabular-nums">{fmtINR(t.total)}</span>
                </div>
              </div>
              {perPlate > 0 && (
                <div className="mx-3 mb-3 rounded-xl px-4 py-3 flex items-center justify-between text-white" style={{ background: 'linear-gradient(120deg,#0f766e,#16a085)' }}>
                  <span className="flex items-center gap-2 text-[12.5px] font-semibold uppercase tracking-wide">
                    <i className="fas fa-utensils text-[13px]" /> Per Plate
                    <span className="text-white/60 normal-case tracking-normal font-medium">· {ppGuests} guests</span>
                  </span>
                  <span className="text-[22px] font-extrabold leading-none">{fmtINR(perPlate)}</span>
                </div>
              )}
            </div>
          </div>
        )
      })}

      <div>
        <div className="text-[11px] font-semibold mb-1.5 uppercase tracking-wide text-slate-500">Misc Expenses <span className="text-slate-400 font-normal normal-case">· applies to whole quote</span></div>
        <ExpenseEditor items={overallExpenses} setItems={setOverallExpenses} title="Misc Expenses" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-bold text-slate-700 flex items-center gap-2">
            <i className="fas fa-coins text-[#1abc9c]" /> Round Off
          </span>
          <span className={`text-[13px] font-bold ${finalRoundOff < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
            {finalRoundOff === 0 ? '—' : `${finalRoundOff > 0 ? '+' : ''}${fmtINR(finalRoundOff)}`}
          </span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {[...ROUND_MODES, { v: 'none', label: 'None' }].map((m) => (
            <button
              key={m.v}
              type="button"
              onClick={() => setFinalRound({ ...finalRound, mode: m.v })}
              className={`h-10 rounded-xl text-[12px] font-bold border-2 transition ${
                finalRound.mode === m.v ? 'bg-[#1abc9c] text-white border-[#1abc9c]' : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {finalRound.mode === 'manual' && (
          <Field label="Manual Round-off (+/−)">
            <input
              type="number"
              inputMode="numeric"
              className="qv2-input"
              placeholder="0"
              value={showNum(finalRound.manual)}
              onChange={(e) => setFinalRound({ ...finalRound, manual: toNum(e.target.value) })}
            />
          </Field>
        )}
      </div>

      <div className="rounded-2xl p-4 text-white" style={{ background: 'linear-gradient(135deg,#0f766e,#134e4a)' }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-white/70 text-[13px]">Subtotal before profit <span className="text-white/40">(your cost)</span></span>
          <span className="font-semibold">{fmtINR(grandPreProfit)}</span>
        </div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-white/70 text-[13px]">Total profit ({grandProfitPct}%)</span>
          <span className={`font-semibold ${grandProfit < 0 ? 'text-rose-300' : 'text-emerald-300'}`}>{fmtINR(grandProfit)}</span>
        </div>
        {finalRoundOff !== 0 && (
          <div className="flex items-center justify-between mb-1">
            <span className="text-white/70 text-[13px]">Round off</span>
            <span className="font-semibold">{finalRoundOff > 0 ? '+' : ''}{fmtINR(finalRoundOff)}</span>
          </div>
        )}
        <div className="border-t border-white/15 my-2" />
        <div className="flex items-center justify-between">
          <span className="font-bold text-lg">Grand Total</span>
          <span className="text-2xl font-extrabold text-[#3fe0c5]">{fmtINR(grandTotal)}</span>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">PDF Format</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setPdfMode('client')}
            className={`rounded-xl border-2 p-3 text-left transition ${pdfMode === 'client' ? 'border-[#1abc9c] bg-[#1abc9c]/5' : 'border-slate-200'}`}
          >
            <div className="font-bold text-[13px] flex items-center gap-1.5">
              {pdfMode === 'client' && <i className="fas fa-circle-check text-[#1abc9c] text-[12px]" />}Client
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Items only, no prices — just the final amount.</div>
          </button>
          <button
            type="button"
            onClick={() => setPdfMode('detailed')}
            className={`rounded-xl border-2 p-3 text-left transition ${pdfMode === 'detailed' ? 'border-[#1abc9c] bg-[#1abc9c]/5' : 'border-slate-200'}`}
          >
            <div className="font-bold text-[13px] flex items-center gap-1.5">
              {pdfMode === 'detailed' && <i className="fas fa-circle-check text-[#1abc9c] text-[12px]" />}Detailed
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Every line price + per-service totals.</div>
          </button>
        </div>
      </div>

      <div className="text-center text-[11px] text-slate-400 pt-1">
        Tap <span className="font-semibold text-slate-500">Preview &amp; Download</span> below to save the
        {' '}<span className="font-semibold text-slate-500">{pdfMode === 'client' ? 'Client' : 'Detailed'}</span> PDF.
      </div>
    </div>
  )
}

// ─── Printable summary — designed single A4 page (captured to 1-page PDF) ─────
const PrintSummary = ({ event, quoteNo, selectedServices, serviceTotals, catLines, snackLines, decorLines, decorGender, photoLines, entLines, cakeLines, giftLines, grandTotal, grandProfit, grandProfitPct, grandPreProfit, expenses = {}, overallExpensesTotal = 0, guests, snackGuests, catGuestSplit, cateringPerPlate, mode = 'client' }) => {
  const linesFor = { Catering: catLines, 'Snacks & Stalls': snackLines, Decoration: decorLines, Photography: photoLines, Entertainment: entLines, Cake: cakeLines, 'Return Gifts': giftLines }
  // Catering line → section label (mirrors the on-screen Review grouping).
  const catGrp = (l) => (l?.veg === false ? 'Non-Veg' : l?.veg === true ? 'Veg' : l?.veg == null ? 'Common' : null)
  const catGrpGuests = (g) => (!catGuestSplit ? null : g === 'Non-Veg' ? catGuestSplit.nonveg : g === 'Veg' ? catGuestSplit.veg : catGuestSplit.total)
  // "Common" heading only when both Non-Veg and Veg are present.
  const bothCatSections = catGuestSplit && catGuestSplit.nonveg > 0 && catGuestSplit.veg > 0
  const displayServices = selectedServices.filter((k) => (linesFor[k] || []).length > 0)
  const detailed = mode === 'detailed'
  const isPkg = event.quoteType === 'package'
  const decorShown = displayServices.includes('Decoration')
  const showGuests = displayServices.includes('Catering') || displayServices.includes('Snacks & Stalls')
  const dateStr = event.date
    ? `${new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} (${new Date(event.date).toLocaleDateString('en-IN', { weekday: 'short' })})`
    : ''
  const timeStr = fmtTime12(event.startTime) && fmtTime12(event.endTime) ? `${fmtTime12(event.startTime)} – ${fmtTime12(event.endTime)}` : fmtTime12(event.startTime)

  const Tile = ({ label, value, sub }) => (
    <div style={{ background: '#fff', border: '1px solid #e6eef0', borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ fontSize: 8.5, letterSpacing: 1.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 3, lineHeight: 1.3 }}>{value || '—'}</div>
      {sub && <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 1 }}>{sub}</div>}
    </div>
  )

  const RedBadge = ({ text }) => (
    <span style={{ color: '#e11d48', fontWeight: 800, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>★ {text}</span>
  )

  // Body: inclusion lists flow inline with separators; named items sit in columns.
  const ServiceBody = ({ lines, serviceKey }) => {
    const groups = []
    const nameLines = []
    lines.forEach((l) => {
      if (l.items?.length) groupItems(l.items).forEach((g) => groups.push(g))
      else nameLines.push(l)
    })
    const renderNameLine = (l, i) => {
      const qtyNote = l.item
        ? `${l.qty} ${l.unitLabel} × ${fmtINR(l.unitPrice)}`
        : l.qty > 1
          ? `${l.qty} × ${fmtINR(l.price)}`
          : ''
      return (
        <div key={i} style={{ fontSize: 11.5, lineHeight: 1.5, color: '#334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ flex: 1 }}>
              <span style={{ color: '#1abc9c', fontWeight: 700 }}>›</span> <span style={{ fontWeight: l.bold ? 800 : 400, color: l.bold ? '#0f172a' : '#334155' }}>{l.name || l.item?.name || 'Item'}</span>
              {l.label && <> <RedBadge text={l.label} /></>}
            </span>
            {detailed && <span style={{ fontWeight: 700, color: '#0f766e', whiteSpace: 'nowrap' }}>{fmtINR(l.total)}</span>}
          </div>
          {(l.description || (detailed && qtyNote)) && (
            <div style={{ fontSize: 9.5, color: '#94a3b8', paddingLeft: 12 }}>
              {detailed && qtyNote}
              {detailed && qtyNote && l.description ? ' · ' : ''}
              {l.description && <span style={{ fontStyle: 'italic' }}>{l.description}</span>}
            </div>
          )}
        </div>
      )
    }
    const NameGrid = ({ items, mt, dense }) => {
      const cols = dense
        ? items.length >= 5 ? 3 : items.length >= 2 ? 2 : 1
        : items.length > 8 ? 3 : items.length > 3 ? 2 : 1
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, columnGap: 20, rowGap: 2, marginTop: mt }}>
          {items.map((l) => renderNameLine(l, l._i))}
        </div>
      )
    }
    // For Catering, split the dishes into Non-Veg / Veg / Common sub-sections.
    const catSections =
      serviceKey === 'Catering'
        ? (() => {
            const order = ['Non-Veg', 'Veg', 'Common']
            const by = {}
            nameLines.forEach((l, i) => {
              const g = catGrp(l) || 'Common'
              ;(by[g] = by[g] || []).push({ ...l, _i: i })
            })
            return order.filter((g) => by[g]?.length).map((g) => ({ g, items: by[g] }))
          })()
        : null
    return (
      <div style={{ padding: '8px 16px 10px' }}>
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginBottom: 3 }}>
            {g.section && <div style={{ fontSize: 9.5, fontWeight: 800, color: '#0f766e', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 1 }}>{g.section}</div>}
            <div style={{ fontSize: 11.5, lineHeight: 1.85, color: '#334155' }}>
              {g.entries.filter((e) => e.text).map((e, ei) => (
                <span key={ei}>
                  {ei > 0 && <span style={{ color: '#1abc9c', fontWeight: 700 }}>{'   •   '}</span>}
                  <span style={{ fontWeight: e.bold ? 700 : 400, color: e.bold ? '#0f172a' : '#334155' }}>{e.text}</span>
                </span>
              ))}
            </div>
          </div>
        ))}
        {catSections
          ? catSections.map(({ g, items }, si) => {
              const gc = catGrpGuests(g)
              // Drop the Common heading when only one section (Non-Veg/Veg) exists.
              const hideHeading = g === 'Common' && !bothCatSections
              return (
                <div key={g} style={{ marginTop: si === 0 && !groups.length ? 0 : hideHeading ? 0 : 6 }}>
                  {!hideHeading && (
                    <div style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 2, color: g === 'Non-Veg' ? '#e11d48' : g === 'Veg' ? '#16a34a' : '#64748b' }}>
                      {g}{gc != null ? ` · ${gc} Guests` : ''}
                    </div>
                  )}
                  <NameGrid items={items} mt={0} dense />
                </div>
              )
            })
          : nameLines.length > 0 && <NameGrid items={nameLines.map((l, i) => ({ ...l, _i: i }))} mt={groups.length ? 5 : 0} />}
      </div>
    )
  }

  // ── Creative CLIENT layout — big fonts, hero image, condensed content ──
  if (!detailed) {
    const fullName = isPkg
      ? event.packageName || 'Package'
      : [event.salutation ? `${event.salutation}.` : '', event.guestName].filter(Boolean).join(' ') || 'Valued Guest'
    // Returns rows: { text, bold, section, label }  (section rows are headings)
    const clientItems = (key) => {
      const out = []
      let lastGrp = null
      ;(linesFor[key] || []).forEach((l) => {
        // For Catering, emit a Non-Veg / Veg / Common heading (with guest count)
        // at each section boundary — mirrors the on-screen Review grouping.
        if (key === 'Catering') {
          const g = catGrp(l)
          if (g && g !== lastGrp) {
            const gc = catGrpGuests(g)
            // Non-Veg / Veg show their own guest count; Common shows none.
            // Skip the Common heading entirely when only one section is present.
            const showCount = g !== 'Common' && gc != null
            if (!(g === 'Common' && !bothCatSections)) {
              out.push({ text: `${g}${showCount ? ` · ${gc} Guests` : ''}`, section: true, catgrp: g })
            }
            lastGrp = g
          }
        }
        if (l.items?.length) {
          groupItems(l.items).forEach((g) => {
            if (g.section) out.push({ text: g.section, section: true })
            g.entries.filter((e) => e.text).forEach((e) => out.push({ text: e.text, bold: e.bold }))
          })
        } else {
          out.push({
            text: [l.name || l.item?.name, l.description].filter(Boolean).join(' — '),
            bold: l.bold,
            label: l.label,
          })
        }
      })
      return out
    }
    const SERVICE_FA = {
      Catering: 'fa-utensils',
      'Snacks & Stalls': 'fa-ice-cream',
      Decoration: 'fa-wand-magic-sparkles',
      Photography: 'fa-camera',
      Entertainment: 'fa-masks-theater',
      Cake: 'fa-cake-candles',
      'Return Gifts': 'fa-gift',
    }
    // [primary, fallback] — falls back to an existing asset if the new file isn't added yet.
    const PHOTOS = [
      ['/visuals/decor-hero.png', '/visuals/lobby-decoration-1.png'],
      ['/visuals/food-biriyani.png', '/visuals/lobby-decoration-2.png'],
      ['/visuals/led-sound-1.jpg', '/visuals/photobooth-1.jpg'],
    ]
    const metaLine = isPkg
      ? ''
      : [
          `${event.eventType}${decorGender && !decorShown ? ` · ${decorGender}` : ''}`,
          dateStr,
          timeStr,
          event.location,
        ].filter(Boolean).join('  ·  ')
    return (
      <div style={{ width: 794, background: '#fff', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', color: '#0f172a', boxSizing: 'border-box' }}>
        {/* Header + package price */}
        <div style={{ position: 'relative', background: 'linear-gradient(125deg,#0f766e 0%,#1abc9c 100%)', color: '#fff', padding: '26px 44px 30px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: -70, top: -70, width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'absolute', left: -50, bottom: -80, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ background: '#fff', borderRadius: 14, padding: '9px 12px', boxShadow: '0 6px 18px rgba(0,0,0,0.2)', height: 58, display: 'flex', alignItems: 'center' }}>
                <img src="/assets/images/logo.png" alt="AuraSquare" style={{ height: 40, display: 'block' }} />
              </div>
              <div style={{ height: 58, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.15 }}>AuraSquare Event Management</div>
                <div style={{ fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.9, marginTop: 3, lineHeight: 1 }}>Your Vision, Our Aura</div>
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.9 }}>
              Event Quotation<br /><span style={{ opacity: 0.85 }}>{quoteNo}</span>
            </div>
          </div>

          {/* Package price box */}
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <div style={{ border: '2px solid rgba(255,255,255,0.55)', borderRadius: 16, padding: '12px 44px 16px', textAlign: 'center', background: 'rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 5, textTransform: 'uppercase', opacity: 0.85, lineHeight: 1 }}>Package</div>
              <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1, marginTop: 2 }}>{fmtINR(grandTotal)}</div>
            </div>
          </div>
          <div style={{ position: 'relative', textAlign: 'center', marginTop: isPkg ? 18 : 14 }}>
            {isPkg ? (
              <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', opacity: 0.85, marginBottom: 6 }}>Package</div>
                <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', lineHeight: 1.1 }}>{fullName}</div>
                <div style={{ width: 70, height: 3, background: 'rgba(255,255,255,0.75)', borderRadius: 2, marginTop: 10 }} />
              </div>
            ) : (
              <>
                <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.2 }}>{fullName}</div>
                {metaLine && <div style={{ fontSize: 13, opacity: 0.94, marginTop: 4 }}>{metaLine}</div>}
              </>
            )}
          </div>
        </div>

        {/* Services — two balanced columns (distribute by height) */}
        {(() => {
          const blocks = displayServices.map((key) => ({ key, items: clientItems(key) }))
          // weight ≈ header (2 rows) + one row per item
          const weight = (b) => 2 + b.items.length
          const colA = []
          const colB = []
          let hA = 0
          let hB = 0
          blocks.forEach((b) => {
            if (hA <= hB) { colA.push(b); hA += weight(b) } else { colB.push(b); hB += weight(b) }
          })
          const renderBlock = (b) => {
            // Catering shows per-section counts (Non-Veg/Veg) instead of a total.
            const guestNote = b.key === 'Snacks & Stalls' && snackGuests > 0 ? ` — ${snackGuests} Guests` : ''
            const genderNote = b.key === 'Decoration' && decorGender ? ` · ${decorGender}` : ''
            return (
              <div key={b.key} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <i className={`fas ${SERVICE_FA[b.key] || 'fa-star'}`} style={{ color: '#1abc9c', fontSize: 22, width: 26, textAlign: 'center' }} />
                  <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#0f766e' }}>
                    {b.key}
                    {(guestNote || genderNote) && <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: '#64748b' }}>{guestNote}{genderNote}</span>}
                  </span>
                </div>
                {b.items.map((it, ti) =>
                  it.catgrp ? (
                    // Non-Veg / Veg / Common heading — colour-coded with a veg marker.
                    <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 11, marginBottom: 5 }}>
                      {it.catgrp !== 'Common' && (
                        <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${it.catgrp === 'Non-Veg' ? '#e11d48' : '#16a34a'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: it.catgrp === 'Non-Veg' ? '#e11d48' : '#16a34a' }} />
                        </span>
                      )}
                      <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: it.catgrp === 'Non-Veg' ? '#e11d48' : it.catgrp === 'Veg' ? '#16a34a' : '#64748b' }}>{it.text}</span>
                    </div>
                  ) : it.section ? (
                    <div key={ti} style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: '#0f766e', marginTop: 9, marginBottom: 4 }}>{it.text}</div>
                  ) : (
                    <div key={ti} style={{ display: 'flex', gap: 11, fontSize: 16.5, lineHeight: 1.55, marginBottom: 4, color: it.bold ? '#0f172a' : '#334155', fontWeight: it.bold ? 700 : 400 }}>
                      <span style={{ color: '#1abc9c', fontWeight: 700, marginTop: 1 }}>•</span>
                      <span>
                        {it.text}
                        {it.label && <span style={{ color: '#e11d48', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}> ★ {it.label}</span>}
                      </span>
                    </div>
                  ),
                )}
              </div>
            )
          }
          return (
            <div style={{ padding: '26px 44px 4px', display: 'flex', gap: 36, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>{colA.map(renderBlock)}</div>
              <div style={{ flex: 1 }}>{colB.map(renderBlock)}</div>
            </div>
          )
        })()}

        {/* Photo strip */}
        <div style={{ padding: '6px 44px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {PHOTOS.map(([src, fallback], i) => (
            <div key={i} style={{ height: 150, borderRadius: 16, overflow: 'hidden', boxShadow: '0 6px 16px rgba(15,118,110,0.15)' }}>
              <img
                src={src}
                onError={(e) => { if (fallback && e.currentTarget.src.indexOf(fallback) === -1) e.currentTarget.src = fallback }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                alt=""
              />
            </div>
          ))}
        </div>

        {/* Call footer — inset, rounded */}
        <div style={{ padding: '10px 90px 4px' }}>
          <div style={{ background: 'linear-gradient(125deg,#0f766e 0%,#134e4a 100%)', color: '#fff', padding: '20px 30px', textAlign: 'center', borderRadius: 20 }}>
            <div style={{ fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', opacity: 0.8 }}>Book your celebration</div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 0.5, marginTop: 5 }}>CALL&nbsp; +91 9444 848 616</div>
            <div style={{ fontSize: 12, opacity: 0.92, marginTop: 8 }}>
              aurasquare@zohomail.in&nbsp; ·&nbsp;
              <span data-pdflink="https://aurasquare.co.in">aurasquare.co.in</span>&nbsp; ·&nbsp;
              <span data-pdflink="https://www.instagram.com/aurasquare.events"><i className="fab fa-instagram" style={{ marginRight: 3 }} />aurasquare.events</span>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: 10.5, color: '#94a3b8', padding: '8px 44px 14px' }}>
          Prices are indicative &amp; subject to confirmation · Valid 15 days from date of issue
        </div>
      </div>
    )
  }

  return (
    <div style={{ width: 794, background: '#fff', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', color: '#0f172a', boxSizing: 'border-box' }}>
      {/* Header band */}
      <div style={{ position: 'relative', background: 'linear-gradient(120deg,#0f766e 0%,#1abc9c 100%)', color: '#fff', padding: '18px 32px 16px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -60, top: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', right: 60, bottom: -90, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: '8px 11px', boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
              <img src="/assets/images/logo.png" alt="AuraSquare Events" style={{ height: 34, display: 'block' }} />
            </div>
            <div style={{ lineHeight: 1.55 }}>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.2 }}>AuraSquare Event Management</div>
              <div style={{ fontSize: 10, opacity: 0.92, marginTop: 3 }}>24, Mettu Street, Kundrathur, Chennai 600069</div>
              <div style={{ fontSize: 10, opacity: 0.92 }}>
                +91 9444 848 616 &nbsp;·&nbsp; aurasquare@zohomail.in &nbsp;·&nbsp;
                <span data-pdflink="https://aurasquare.co.in">aurasquare.co.in</span> &nbsp;·&nbsp;
                <span data-pdflink="https://www.instagram.com/aurasquare.events"><i className="fab fa-instagram" style={{ marginRight: 3 }} />aurasquare.events</span>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 24, padding: '0 15px', background: 'rgba(255,255,255,0.22)', borderRadius: 30, fontSize: 10, fontWeight: 800, letterSpacing: 2.5, lineHeight: 1 }}>QUOTATION</div>
            <div style={{ fontSize: 11.5, marginTop: 9, opacity: 0.95 }}>{quoteNo}</div>
            {!isPkg && dateStr && <div style={{ fontSize: 11.5, opacity: 0.9 }}>{dateStr}</div>}
          </div>
        </div>
      </div>

      {/* Content flows top-down directly under the header */}
      <div>
      {/* Event info tiles */}
      <div style={{ padding: '14px 32px 2px' }}>
        {event.quoteType === 'package' ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '10px 14px', background: '#fff', border: '1px solid #e6eef0', borderRadius: 12 }}>
            <span style={{ fontSize: 8.5, letterSpacing: 1.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>Package</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{event.packageName || '—'}</span>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 10 }}>
              <Tile label="Prepared For" value={[event.salutation ? `${event.salutation}.` : '', event.guestName].filter(Boolean).join(' ') || '—'} sub={`${event.contactCode || ''} ${event.contact || ''}`.trim()} />
              <Tile label="Occasion" value={`${event.eventType || '—'}${decorGender && !decorShown ? ` · ${decorGender}` : ''}`} sub={showGuests ? `${guests} guests` : undefined} />
              <Tile label="When" value={dateStr} sub={timeStr} />
            </div>
            {event.location && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 10, padding: '6px 12px', background: '#fff', border: '1px solid #e6eef0', borderRadius: 10 }}>
                <span style={{ fontSize: 8.5, letterSpacing: 1.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>Venue</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{event.location}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Services — full-width stacked sections, items flow across columns */}
      <div style={{ padding: '10px 32px 6px' }}>
        {displayServices.map((key) => {
          const t = serviceTotals[key]
          const lines = linesFor[key]
          const ppGuests = key === 'Catering' ? catGuestSplit?.total || guests : key === 'Snacks & Stalls' ? snackGuests : 0
          const perPlate = ppGuests > 0 && (key === 'Catering' || key === 'Snacks & Stalls') ? Math.round(t.total / ppGuests) : 0
          return (
            <div key={key} style={{ marginBottom: 8, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', breakInside: 'avoid' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdfa', padding: '7px 15px', borderLeft: '4px solid #1abc9c', borderBottom: '1px solid #d5f5ee' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13.5, color: '#0f766e' }}>
                  {key}
                  {perPlate > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#0f766e', color: '#fff', padding: '2px 9px', borderRadius: 30, fontSize: 10, fontWeight: 800, letterSpacing: 0.2 }}>
                      <i className="fas fa-utensils" style={{ fontSize: 8 }} /> {fmtINR(perPlate)}/plate · {ppGuests} guests
                    </span>
                  )}
                  {key === 'Decoration' && decorGender && (
                    <span style={{ fontWeight: 600, color: '#64748b', fontSize: 10.5 }}> · Baby: {decorGender}</span>
                  )}
                </span>
                {detailed && <span style={{ fontWeight: 800, fontSize: 13.5, color: '#0f766e' }}>{fmtINR(t.total)}</span>}
              </div>
              <ServiceBody lines={lines} serviceKey={key} />
              {detailed && (expenses[key] || []).filter((e) => e.name || e.amount).length > 0 && (
                <div style={{ padding: '2px 15px 4px', fontSize: 10, color: '#b45309' }}>
                  {(expenses[key] || []).filter((e) => e.name || e.amount).map((e) => (
                    <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>+ {e.name || 'Expense'}{toNum(e.qty) > 1 ? ` ×${toNum(e.qty)}` : ''}</span>
                      <span style={{ fontWeight: 700 }}>{fmtINR(toNum(e.qty ?? 1) * toNum(e.amount))}</span>
                    </div>
                  ))}
                </div>
              )}
              {detailed && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, padding: '5px 15px', borderTop: '1px dashed #e2e8f0', background: '#fafcfc', fontSize: 10, color: '#64748b' }}>
                  <span>Items <b style={{ color: '#334155' }}>{fmtINR(t.subtotal)}</b></span>
                  {t.expenses > 0 && <span>Other Expense <b style={{ color: '#b45309' }}>{fmtINR(t.expenses)}</b></span>}
                  <span>Subtotal <b style={{ color: '#334155' }}>{fmtINR(t.cost)}</b></span>
                  <span>Profit <b style={{ color: t.totalProfit < 0 ? '#e11d48' : '#16a34a' }}>{fmtINR(t.totalProfit)}</b></span>
                  <span>Total <b style={{ color: '#0f766e' }}>{fmtINR(t.total)}</b> ({t.totalProfitPct}%)</span>
                </div>
              )}
            </div>
          )
        })}
        {detailed && (expenses.overall || []).filter((e) => e.name || e.amount).length > 0 && (
          <div style={{ marginBottom: 8, border: '1px solid #fde68a', borderRadius: 12, overflow: 'hidden', breakInside: 'avoid' }}>
            <div style={{ background: '#fffbeb', padding: '7px 15px', borderLeft: '4px solid #f59e0b', fontWeight: 800, fontSize: 13, color: '#b45309' }}>Misc Expenses</div>
            <div style={{ padding: '6px 15px' }}>
              {(expenses.overall || []).filter((e) => e.name || e.amount).map((e) => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#334155', lineHeight: 1.7 }}>
                  <span>+ {e.name || 'Expense'}{toNum(e.qty) > 1 ? ` ×${toNum(e.qty)}` : ''}</span>
                  <span style={{ fontWeight: 700, color: '#b45309' }}>{fmtINR(toNum(e.qty ?? 1) * toNum(e.amount))}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      </div>{/* end middle */}

      {/* Total band */}
      <div style={{ background: 'linear-gradient(120deg,#0f766e 0%,#134e4a 100%)', color: '#fff', padding: '15px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: 2.5, textTransform: 'uppercase', opacity: 0.75 }}>Grand Total</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>
            {detailed ? (
              <>Cost ₹{Math.round(grandPreProfit).toLocaleString('en-IN')} · Profit <span style={{ color: grandProfit < 0 ? '#fda4af' : '#6ee7b7', fontWeight: 700 }}>{fmtINR(grandProfit)}</span> · Margin {grandProfitPct}%</>
            ) : (
              'Inclusive of all selected services'
            )}
          </div>
        </div>
        <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1 }}>{fmtINR(grandTotal)}</div>
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 32px', fontSize: 9.5, color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #eef2f6' }}>
        Prices are indicative &amp; subject to confirmation · Valid 15 days from date of issue
      </div>
    </div>
  )
}

const GlobalStylesV2 = () => (
  <style>{`
    .qv2-input {
      width: 100%;
      padding: 0.75rem 0.875rem;
      border: 1px solid #cbd5e1;
      border-radius: 0.625rem;
      font-size: 16px;
      background: #fff;
      outline: none;
      transition: border-color .15s, box-shadow .15s;
      -webkit-appearance: none;
      appearance: none;
      color: #0f172a;
    }
    .qv2-input:focus { border-color: #1abc9c; box-shadow: 0 0 0 3px rgba(26,188,156,.18); }
    .qv2-input-error { border-color: #f43f5e !important; background: #fff1f2; }
    select.qv2-input {
      padding-right: 2.25rem;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2364748b' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.875rem center;
    }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { scrollbar-width: none; }

    .qv2-print-area { position: absolute; left: -10000px; top: 0; width: 794px; background: #fff; }
    @media print {
      @page { size: A4; margin: 12mm; }
      html, body { background: #fff !important; }
      .qv2-no-print { display: none !important; }
      .qv2-print-area { position: static !important; left: 0 !important; width: auto !important; display: block !important; }
      .qv2-print-area table { page-break-inside: auto; }
      .qv2-print-area tr { page-break-inside: avoid; }
    }
  `}</style>
)

export default QuoteV2
